import { Box } from '@mui/system';
import Header from '../components/common/Header';
import RemoveBg from '../assets/icons/canvasIcons/remove-bg.tsx';
import ReplaceBg from '../assets/icons/canvasIcons/replace-bg.tsx';
import Erase from '../assets/icons/canvasIcons/erase.tsx';
import inpaint from '../assets/icons/canvasIcons/inpaint.tsx';
import outpaint from '../assets/icons/canvasIcons/outpaint.tsx';
import upscale from '../assets/icons/canvasIcons/upscale.tsx';
import deblur from '../assets/icons/canvasIcons/deblur.tsx';
import denoise from '../assets/icons/canvasIcons/denoise.tsx';
import retouch from '../assets/icons/canvasIcons/retouch.tsx';
import Exiticon from '../assets/icons/canvasIcons/exit.svg';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReplaceBgTool from './Tools/ReplaceBgTool';
import EraseTool from './Tools/EraseTool';
import InpaintTool from './Tools/InpaintTool';
import { useLocation } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import CanvasApi from '../services/CanvasApi.ts';
import { GenerateImageResponse, GenerateImageRequest } from '../types/CanvaGenerateInterface.tsx';
import { useSnackbar } from '../components/context/SnackbarContext.tsx';
import OutpaintTool from './Tools/OutpaintTool.tsx';
import { editorImageclient } from '../utils/apiClients/uploadImageclient.ts';
import getHdImageUrl from '../utils/getHdImageUrl.ts';
import EditorToolbar from './EditorToolbar.tsx';
import CanvasWorkSpace from './CanvasWorkSpace.tsx';
import UndoRedoControls from './UndoRedoControls.tsx';
import { useCanvaSettings } from '../context/useCanvaSettings';
import CanvasModalContainer from '../pages/Canvas/Component/CanvasModalContainer';
import { useAppDispatch } from '../store/store';
import { setOnCanvas } from '../store/slices/gloableState';
import imageGenerationClient from '../utils/apiClients/imagesClient';
import { useImageModels } from '../hooks/useImageModels';
import { resolveModelName, resolveModelData } from '../utils/modelResolution';

type ToolComponentProps = {
  onAction: (newImage?: { id: string; url: string } | string) => void;
  canvasApi: CanvasApi;
  id: string;
  initialPrompt: string;
  subjectImage: string | null;
  pushToHistory: (image: string, imageId: string) => void;
  setIsGenerating: (val: boolean) => void;
  setCurrentImageId?: (val: string) => void;
  onAspectRatioChange?: (aspectRatio: string) => void;
  onDimensionsChange?: (dimensions: { width: number; height: number }) => void;
  currentCanvasDimensions?: { width: number; height: number };
  currentGenerationDimensions?: { width: number; height: number };
  imageCategories: string[];
  setImageCategories: (categories: string[]) => void;
  projectId?: string | null;
  canvasWidth?: string
};

interface canvaProps{
  exiteBoolean?: boolean
  marginBottomEditing?:number
  canvaBoardDisplay:string
  activeTool?: string | null
  setActiveTool?: React.Dispatch<React.SetStateAction<string | null>>
  setToolComponent?: (component: React.ReactNode) => void
  onGeneratedImagesChange?: (images: Array<GenerateImageResponse>) => void
  contentTypeTab?: 'image' | 'video'
}

// Model mode constants
const DEFAULT_GENERATE_MODEL = 'FLUX1.1 [pro]';
const DEFAULT_EDIT_MODEL = 'FLUX_Kontext_Edit';

// Static paint area dimensions
const STATIC_PAINT_AREA_HEIGHT = 500; // Fixed height for paint area

// List of editing tools
const EDITING_TOOLS = ['Remove Bg', 'Replace Bg', 'Erase', 'Inpaint', 'Outpaint', 'Upscale', 'Deblur', 'Denoise', 'Retouch'];

// Helper function to determine if a model is for editing
const isEditModel = (model: string): boolean => {
  const EDIT_MODELS = ['FLUX_Kontext_Edit', 'Canvas_AI_Editor'];
  return EDIT_MODELS.some(editModel => model.includes(editModel) || model.toLowerCase().includes('edit'));
};

// Helper function to get model category
const getModelCategory = (model: string): 'Generate' | 'Edit' => {
  return isEditModel(model) ? 'Edit' : 'Generate';
};

// Helper function to fetch full image/video details including prompt and modelName
const fetchFullMediaDetails = async (
  mediaId: string, 
  mediaType: 'image' | 'video',
  fallbackPrompt: string,
  fallbackModelName: string
): Promise<{ prompt: string; modelName: string }> => {
  try {
    const endpoint = mediaType === 'image' ? `/images/${mediaId}` : `/videos/${mediaId}`;
    const { data: fullData } = await imageGenerationClient.get(endpoint);
    return {
      prompt: fullData?.prompt || fallbackPrompt || '',
      modelName: fullData?.modelName || fallbackModelName || ''
    };
  } catch (error) {
    console.error(`Error fetching full details for ${mediaType} ${mediaId}:`, error);
    // Return fallback values if fetch fails
    return {
      prompt: fallbackPrompt || '',
      modelName: fallbackModelName || ''
    };
  }
};

const  CanvasAIEditor:React.FC<canvaProps> = ({...props}) => {
  // const theme = useTheme();
  // const navigate = useNavigate(); 
  const [internalActiveTool, setInternalActiveTool] = useState<string | null>(null);
  
  // Use props if provided, otherwise use internal state
  const activeTool = props.activeTool !== undefined ? props.activeTool : internalActiveTool;
  const setActiveTool = props.setActiveTool || setInternalActiveTool;

  // Features from /canvas route
  const { canvaSettings, setCanvaSettings } = useCanvaSettings();
  const { sections } = useImageModels();

  // Helper functions to switch modes
  const switchToGenerateMode = () => {
    if (canvaSettings.model !== DEFAULT_GENERATE_MODEL) {
      setCanvaSettings(settings => ({
        ...settings,
        model: DEFAULT_GENERATE_MODEL
      }));
    }
  };

  const switchToEditMode = () => {
    if (canvaSettings.model !== DEFAULT_EDIT_MODEL) {
      setCanvaSettings(settings => ({
        ...settings,
        model: DEFAULT_EDIT_MODEL
      }));
    }
  };

  // Switch to Edit mode when an editing tool is activated only
  useEffect(() => {
    if (activeTool && EDITING_TOOLS.includes(activeTool)) {
      setCanvaSettings(settings => {
        // Only update if model is not already the edit model
        if (settings.model !== DEFAULT_EDIT_MODEL) {
          return {
            ...settings,
            model: DEFAULT_EDIT_MODEL
          };
        }
        return settings;
      });
    }
  }, [activeTool, setCanvaSettings]);

  // Switch to Generate mode when Generate tool is activated
  useEffect(() => {
    if (activeTool === 'Generate') {
      setCanvaSettings(settings => {
        // Only update if model is not already the generate model
        if (settings.model !== DEFAULT_GENERATE_MODEL) {
          return {
            ...settings,
            model: DEFAULT_GENERATE_MODEL
          };
        }
        return settings;
      });
    }
  }, [activeTool, setCanvaSettings]);

  const dispatch = useAppDispatch();

  // Add CSS styles for checkerboard background
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      .checkerboard-bg {
        background-color: #ffffff;
        background-image: 
          repeating-conic-gradient(#e0e0e0 0% 25%, transparent 0% 50%);
        background-size: 32px 32px;
        position: relative;
        overflow: hidden;
        transition: width 0.3s ease, height 0.3s ease;
      }
      .resize-handle {
        position: absolute;
        width: 16px;
        height: 16px;
        background-color: #ffffff;
        border: 2px solid #3b82f6;
        border-radius: 50%;
        z-index: 30;
        box-shadow: 0 2px 4px rgba(0,0,0,0.2);
      }
      .resize-handle.tl { top: -8px; left: -8px; cursor: nwse-resize; z-index: 1001; }
      .resize-handle.tr { top: -8px; right: -8px; cursor: nesw-resize; z-index: 1001; }
      .resize-handle.bl { bottom: -8px; left: -8px; cursor: nesw-resize; }
      .resize-handle.br { bottom: -8px; right: -8px; cursor: nwse-resize; }
    `;
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  const location = useLocation();
  // Don't load image if it's a rerun (play button click)
  const passedImage = location.state?.isRerun ? null : location.state?.image;
  const [, setPassedId] = useState<string>(location.state?.isRerun ? '' : (location.state?.id || ''));
  const [currentImageId, setCurrentImageId] = useState<string | null>(location.state?.isRerun ? null : location.state?.id);
  const [currentItemId, setCurrentItemId] = useState<string | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [prompt, setPrompt] = useState(location.state?.prompt ?? '');
  const [editImagePrompt, setEditImagePrompt] = useState(location.state?.prompt ?? '');
  const [isUploaded, setIsUploaded] = useState<boolean>(false);
  const [selectedAspectRatio, setSelectedAspectRatio] = useState('16:9');
  console.log(selectedAspectRatio);
  const [generationDimensions, setGenerationDimensions] = useState({ width: 1920, height: 1080 });

  const [imageCategories, setImageCategories] = useState<string[]>(location.state?.imageCategories ?? ['vanilla']);
  const [projectId] = useState<string | null>(location.state?.projectId ?? null);

  // Features from /canvas route - set global state
  useEffect(() => {
    dispatch(setOnCanvas(true));
    return () => {
      dispatch(setOnCanvas(false));
    };
  }, [dispatch]);

  // Clear image immediately when isRerun is true (play button click) and scroll to top
  useEffect(() => {
    if (location.state?.isRerun) {
      setUploadedImage(null);
      setCurrentImageId(null);
      setCurrentItemId(null);
      setGeneratedImages([]); // Clear generated images when rerunning
      // Clear from localStorage when rerunning (user explicitly generating new image)
      if (location.pathname === '/fairgen-ai-studio') {
        try {
          localStorage.removeItem('fairgen-ai-studio:generatedImages');
        } catch (error) {
          console.error('Error clearing generated images from localStorage:', error);
        }
      }
      setIsUploaded(false); // Reset upload state
      
      // Scroll to top of paint area when play button is clicked
      setTimeout(() => {
        const canvasArea = document.querySelector('.canvas-main-area') as HTMLElement;
        if (canvasArea) {
          const elementTop = canvasArea.getBoundingClientRect().top;
          const offsetPosition = window.scrollY + elementTop - 100; // 100px offset from top
          window.scrollTo({
            top: Math.max(0, offsetPosition),
            behavior: 'smooth'
          });
        } else {
          // Fallback: scroll to top of page
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }
      }, 100);
    }
  }, [location.state?.isRerun, location.state?.timestamp, location.key, location.pathname]);

  // Features from /canvas route - handle location state for rerun
  useEffect(() => {
    const { state } = location;
    if (state?.isRerun) {
      // Restore prompt from generation settings
      if (state.prompt) {
        setPrompt(state.prompt);
        setEditImagePrompt(state.prompt);
      }
      
      // Restore all generation settings from the original generation
      setCanvaSettings(prev => {
        // Try to resolve model identifier to human-readable name using model registry
        const modelIdentifier = state.modelId || 
                               state.modelIdentifier || 
                               state.model || 
                               state.modelName ||
                               state.modelInput;
        
        // Resolve model name from registry if sections are available
        let resolvedModelName: string | null = null;
        let resolvedModelData = null;
        
        if (sections && sections.length > 0 && modelIdentifier) {
          resolvedModelName = resolveModelName(modelIdentifier, sections);
          resolvedModelData = resolveModelData(modelIdentifier, sections);
        }
        
        // Use resolved name if available, otherwise fall back to state values
        // Helper to check if a string looks like a model ID/URL (contains slashes or is a long identifier)
        const isModelIdOrUrl = (str: string | null | undefined): boolean => {
          if (!str) return false;
          return str.includes('/') || (str.includes('-') && str.length > 30);
        };
        
        // Prioritize resolved name, then state.modelName, then check if state.model is friendly
        const friendlyModelName = resolvedModelName ?? 
          state.modelName ?? 
          (state.modelInput && !isModelIdOrUrl(state.modelInput) ? state.modelInput : null) ??
          (state.model && !isModelIdOrUrl(state.model) ? state.model : null) ??
          prev.modelName;
        
        // Use resolved model data if available
        const finalModelId = resolvedModelData?.id || state.modelId || prev.modelId;
        const finalModelType = resolvedModelData?.modelType || state.modelType || prev.modelType;
        
        return {
          ...prev,
          // Model settings
          modelId: finalModelId,
          // Always use the friendly model name, never the ID/URL
          modelName: friendlyModelName,
          model: friendlyModelName,
          modelType: finalModelType,
          // Image dimensions and aspect ratio
          image_dimensions: state.width && state.height ? `${state.width}x${state.height}` : (state.image_dimensions ?? prev.image_dimensions),
          // When fromEditIcon (Reuse button) or isRerun (Play button), don't set aspectRatio from state - let the form set it to default
          aspectRatio: (state.fromEditIcon || state.isRerun) ? prev.aspectRatio : (state.aspectRatio ?? state.aspect_ratio ?? prev.aspectRatio),
          feAspectRatioMapping: state.feAspectRatioMapping ?? prev.feAspectRatioMapping,
          // Number of images
          number_of_images: state.numImages ?? state.number_of_images ?? state.num_images ?? prev.number_of_images,
          // Resolution (for models like Google Nano Banana Pro)
          resolution: state.resolution ?? prev.resolution,
          // Video settings (if applicable)
          videoModelId: state.videoModelId ?? prev.videoModelId,
          videoModelName: state.videoModelName ?? prev.videoModelName,
          videoModelType: state.videoModelType ?? prev.videoModelType,
          videoDuration: state.videoDuration ?? prev.videoDuration,
          // When fromEditIcon (Reuse button) or isRerun (Play button), don't set videoAspectRatio from state - let the form set it to default
          videoAspectRatio: (state.fromEditIcon || state.isRerun) ? prev.videoAspectRatio : (state.videoAspectRatio ?? prev.videoAspectRatio),
          videoResolution: state.videoResolution ?? prev.videoResolution,
          videoGenerateAudio: state.videoGenerateAudio ?? prev.videoGenerateAudio,
          videoBgmMusic: state.videoBgmMusic ?? prev.videoBgmMusic,
          videoImageUrl: state.videoImageUrl ?? prev.videoImageUrl,
          videoEndImageUrl: state.videoEndImageUrl ?? prev.videoEndImageUrl,
          videoLoop: state.videoLoop ?? prev.videoLoop,
          videoCameraFixed: state.videoCameraFixed ?? prev.videoCameraFixed,
          videoMovementAmplitude: state.videoMovementAmplitude ?? prev.videoMovementAmplitude,
          videoStyle: state.videoStyle ?? prev.videoStyle,
          videoAudioUrl: state.videoAudioUrl ?? prev.videoAudioUrl,
          // Other settings
          pose_and_depth: state.pose_and_depth ?? prev.pose_and_depth,
          canvas_subject: state.canvas_subject ?? state.subject ?? prev.canvas_subject,
          exclude_from_image: state.exclude_from_image ?? state.negative_prompt ?? prev.exclude_from_image,
          prompt_guidance: state.prompt_guidance ?? state.guidance ?? state.cfg_scale ?? prev.prompt_guidance,
          quality: state.quality ?? prev.quality,
          seed: state.seed ?? prev.seed,
          sampler: state.sampler ?? prev.sampler,
          imageFormat: state.imageFormat ?? state.image_format ?? prev.imageFormat,
          // Style and project (if passed as objects)
          style: state.style ?? prev.style,
          project: state.project ?? prev.project
        };
      });
    }
  }, [location, location.state?.isRerun, location.state?.timestamp, setCanvaSettings, sections]);

  // Re-resolve model name when sections become available (fallback for when sections weren't loaded during initial rerun)
  useEffect(() => {
    if (!location.state?.isRerun || !sections || sections.length === 0) return;
    
    const modelIdentifier = canvaSettings.modelId || 
                           canvaSettings.model || 
                           canvaSettings.modelName;
    
    // Only re-resolve if current model name looks like an identifier/URL
    const isModelIdOrUrl = (str: string | null | undefined): boolean => {
      if (!str) return false;
      return str.includes('/') || (str.includes('-') && str.length > 30);
    };
    
    if (modelIdentifier && isModelIdOrUrl(canvaSettings.modelName || canvaSettings.model)) {
      const resolvedModelName = resolveModelName(modelIdentifier, sections);
      const resolvedModelData = resolveModelData(modelIdentifier, sections);
      
      if (resolvedModelName) {
        setCanvaSettings(prev => ({
          ...prev,
          modelName: resolvedModelName,
          model: resolvedModelName,
          modelId: resolvedModelData?.id || prev.modelId,
          modelType: resolvedModelData?.modelType || prev.modelType
        }));
      }
    }
  }, [sections, location, canvaSettings.modelId, canvaSettings.model, canvaSettings.modelName, setCanvaSettings]);


  // console.log('imgggggg', imageCategory);

  const canvasApiRef = useRef<CanvasApi | null>(null);
  const { showSnackbar } = useSnackbar();
  const [uploadedImage, setUploadedImage] = useState<string | null>(passedImage || null);
  const [isGenerating, setIsGenerating] = useState(false);
  
  // Check if we're on /fairgen-ai-studio route for persistence
  const isFairgenStudioRoute = location.pathname === '/fairgen-ai-studio';
  const isInitializedRef = useRef(false);
  const previousModelIdRef = useRef<string | null>(null);
  
  // State to track all generated images (for stacking like /canvas route)
  // Note: This array can contain both images and videos, which are filtered in CanvasWorkSpace based on contentTypeTab
  const [generatedImages, setGeneratedImages] = useState<Array<GenerateImageResponse>>([]);
  // State to track view mode (grid or list)
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('list');

  // Destructure callback prop to avoid exhaustive-deps warning
  const { onGeneratedImagesChange } = props;

  // Helper function to get storage key for a specific model
  const getStorageKeyForModel = useCallback((modelId: string | null) => {
    if (!modelId) return null;
    return `fairgen-ai-studio:generatedImages:${modelId}`;
  }, []);
  
  // Helper function to restore images for a specific model
  const restoreImagesForModel = useCallback((modelId: string | null) => {
    if (!isFairgenStudioRoute || !modelId) return;
    
    // Don't restore persisted images if we're loading an image for editing (from Edit icon)
    // This ensures the correct image is shown, not previously generated ones
    const imageIdFromState = location.state?.id;
    const isRerun = location.state?.isRerun;
    if (imageIdFromState && !isRerun) {
      // If we have an image ID from state (edit button click), don't restore persisted images
      // The image loading effect will handle setting the correct image
      return;
    }
    
    const storageKey = getStorageKeyForModel(modelId);
    if (!storageKey) return;
    
    try {
      const persistedImagesStr = localStorage.getItem(storageKey);
      if (persistedImagesStr) {
        const persistedImages = JSON.parse(persistedImagesStr);
        if (Array.isArray(persistedImages) && persistedImages.length > 0) {
          setGeneratedImages(persistedImages);
          // Also set the uploadedImage and currentImageId from the first persisted image for display
          const firstImage = persistedImages[0]?.images?.[0];
          if (firstImage?.url && firstImage?.id) {
            setUploadedImage(firstImage.url);
            setCurrentImageId(firstImage.id);
          }
          return;
        }
      }
    } catch (error) {
      console.error('Error reading persisted generated images from localStorage:', error);
    }
    
    // No images found for this model - clear state
    setGeneratedImages([]);
    setUploadedImage(null);
    setCurrentImageId(null);
  }, [isFairgenStudioRoute, getStorageKeyForModel, location.state]);

  // Restore generated images from localStorage on mount (only for /fairgen-ai-studio route)
  useEffect(() => {
    if (isInitializedRef.current) return;
    if (!isFairgenStudioRoute) return;
    // Don't restore if it's a rerun (play button click) - let the rerun effect handle clearing
    if (location.state?.isRerun) {
      isInitializedRef.current = true;
      previousModelIdRef.current = canvaSettings.modelId;
      return;
    }
    
    // Don't restore if we're loading an image for editing (from Edit icon)
    // The image loading effect will handle setting the correct image
    const imageIdFromState = location.state?.id;
    if (imageIdFromState) {
      isInitializedRef.current = true;
      previousModelIdRef.current = canvaSettings.modelId;
      return;
    }

    // Store initial modelId and restore images for it
    previousModelIdRef.current = canvaSettings.modelId;
    restoreImagesForModel(canvaSettings.modelId);
    isInitializedRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run only once on mount - intentionally exclude dependencies to run only once

  // Switch images when model changes (only for /fairgen-ai-studio route)
  useEffect(() => {
    if (!isFairgenStudioRoute) return;
    if (!isInitializedRef.current) return; // Don't switch during initial mount

    const currentModelId = canvaSettings.modelId;
    const previousModelId = previousModelIdRef.current;

    // If model has changed, switch to images for the new model
    if (currentModelId !== previousModelId) {
      // Restore images for the new model (or clear if none exist)
      restoreImagesForModel(currentModelId);
      // Update previous modelId
      previousModelIdRef.current = currentModelId;
    }
  }, [canvaSettings.modelId, isFairgenStudioRoute, restoreImagesForModel]);

  // Save generated images to localStorage when they change (only for /fairgen-ai-studio route)
  // Save to model-specific key
  useEffect(() => {
    if (!isInitializedRef.current) return; // Don't save during initial restoration
    if (!isFairgenStudioRoute) return;

    const currentModelId = canvaSettings.modelId;
    const storageKey = getStorageKeyForModel(currentModelId);
    
    if (!storageKey) return; // Can't save without modelId

    try {
      if (generatedImages.length > 0) {
        // Save to model-specific key
        localStorage.setItem(storageKey, JSON.stringify(generatedImages));
      } else {
        // Clear from localStorage if no images for this model
        localStorage.removeItem(storageKey);
      }
    } catch (error) {
      console.error('Error saving generated images to localStorage:', error);
    }
  }, [generatedImages, canvaSettings.modelId, isFairgenStudioRoute, getStorageKeyForModel]);

  // Notify parent when generatedImages changes
  useEffect(() => {
    if (onGeneratedImagesChange) {
      onGeneratedImagesChange(generatedImages);
    }
  }, [generatedImages, onGeneratedImagesChange]);
  const [hasNextPrev, setHasNextPrev] = useState({
    next: true,
    previous: true
  });
  const [stepsFromHead, setStepsFromHead] = useState(0);
  const [canvasDimensions, setCanvasDimensions] = useState({ width: 400, height: 300 });
  const [imageBox, setImageBox] = useState({ x: 0, y: 0, width: 400, height: 300 });
  const [imageSize, setImageSize] = useState({ width: 0, height: 0 });
  const [isOutpaintActive, setIsOutpaintActive] = useState(false);
  const [isOutpaintFrameLocked, setIsOutpaintFrameLocked] = useState(false);
  const [hasOutpaintAspect, setHasOutpaintAspect] = useState(false);
  const [hasOutpaintEverGenerated, setHasOutpaintEverGenerated] = useState(false);
  console.log(hasOutpaintEverGenerated);

  const getSessionId = () => {
    const sessionId = localStorage.getItem('sessionId');
    if (!sessionId) {
      const newSessionId = Math.random().toString(36).substring(2, 15);
      localStorage.setItem('sessionId', newSessionId);
      return newSessionId;
    }
    return sessionId;
  };

  const clearSession = async () => {
    try {
      await editorImageclient.clearQueueSesstion({ sessionId: getSessionId() });
    } catch (error) {
      console.error('Error clearing session:', error);
    }
    localStorage.removeItem('sessionId');
  };

  const pushToHistory = useCallback(
    async (newImage: string, newImageId: string) => {
      try {
        setIsGenerating(true);

        if (stepsFromHead < 0 || hasNextPrev?.next) {
          try {
            await editorImageclient.resumeQueue({
              targetImageId: currentImageId || undefined,
              targetItemId: currentItemId || undefined
            });
          } catch (e) {
            console.error('Error resuming queue (safe to proceed):', e);
          }
        }
        const res = await editorImageclient.addImageToQueue({
          imageId: newImageId,
          imageUrl: newImage,
          sessionId: getSessionId()
        });

        if (res.data) {
          setUploadedImage(res.data.item.url);
          setCurrentImageId(res.data.item.imageId);
          setCurrentItemId(res.data.item.id);
          setStepsFromHead(0);
        }
      } catch (error) {
        console.error('Error pushing to history:', error);
      } finally {
        setIsGenerating(false);
      }
    },
    [stepsFromHead, hasNextPrev?.next, currentImageId, currentItemId]
  );

  const handleUndo = async () => {
    try {
      if (-stepsFromHead >= 10) return;
      setIsGenerating(true);
      console.log('Undo - currentItemId:', currentItemId, 'currentImageId:', currentImageId);
      const { data } = await editorImageclient.getImageFromQueuePrevious(currentItemId || '', currentImageId || '');
      console.log('Undo response:', data);
      if (data.item) {
        setCurrentItemId(data.item.id);
        setCurrentImageId(data.item.imageId);
        setHdImage(data.item.imageId, data.item.imageUrl);
        setStepsFromHead(prev => prev - 1);
        setIsUploaded(false);
        if (isOutpaintActive) {
          try {
            const newImageSize = await getImageSize(data.item.imageUrl);
            setImageSize(newImageSize);

            const mainArea = document.querySelector('.canvas-main-area');
            if (mainArea) {
              const containerWidth = mainArea.clientWidth * 0.95;
              const containerHeight = STATIC_PAINT_AREA_HEIGHT;

              setCanvasDimensions({ width: containerWidth, height: containerHeight });

              const imageRatio = newImageSize.width / newImageSize.height;
              let displayWidth, displayHeight;

              if (containerWidth / containerHeight > imageRatio) {
                displayHeight = containerHeight;
                displayWidth = displayHeight * imageRatio;
              } else {
                displayWidth = containerWidth;
                displayHeight = displayWidth / imageRatio;
              }

              setImageBox({
                x: (containerWidth - displayWidth) / 2,
                y: (containerHeight - displayHeight) / 2,
                width: displayWidth,
                height: displayHeight
              });
            }
          } catch (error) {
            console.error('Error updating image dimensions after undo:', error);
          }
        }
      }
      setHasNextPrev({
        next: data.hasNext,
        previous: data.hasPrevious
      });
      console.log(data, 'handleUndo');
    } catch (error) {
      console.error('Error during undo operation:', error);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleRedo = async () => {
    try {
      if (stepsFromHead >= 0) return;
      setIsGenerating(true);
      console.log('Redo - currentItemId:', currentItemId, 'currentImageId:', currentImageId);
      const { data } = await editorImageclient.getImageFromQueueNext(currentItemId || '', currentImageId || '');
      console.log('Redo response:', data);
      if (data.item) {
        setCurrentItemId(data.item.id);
        setCurrentImageId(data.item.imageId);
        setHdImage(data.item.imageId, data.item.imageUrl);
        setStepsFromHead(prev => prev + 1);
        setIsUploaded(false);

        if (isOutpaintActive) {
          try {
            const newImageSize = await getImageSize(data.item.imageUrl);
            setImageSize(newImageSize);

            const mainArea = document.querySelector('.canvas-main-area');
            if (mainArea) {
              const containerWidth = mainArea.clientWidth * 0.95;
              const containerHeight = STATIC_PAINT_AREA_HEIGHT;

              setCanvasDimensions({ width: containerWidth, height: containerHeight });

              const imageRatio = newImageSize.width / newImageSize.height;
              let displayWidth, displayHeight;

              if (containerWidth / containerHeight > imageRatio) {
                displayHeight = containerHeight;
                displayWidth = displayHeight * imageRatio;
              } else {
                displayWidth = containerWidth;
                displayHeight = displayWidth / imageRatio;
              }

              setImageBox({
                x: (containerWidth - displayWidth) / 2,
                y: (containerHeight - displayHeight) / 2,
                width: displayWidth,
                height: displayHeight
              });
            }
          } catch (error) {
            console.error('Error updating image dimensions after undo:', error);
          }
        }
      }
      setHasNextPrev({
        next: data.hasNext,
        previous: data.hasPrevious
      });
      console.log(data, 'handleRedo');
    } catch (error) {
      console.error('Error during redo operation:', error);
    } finally {
      setIsGenerating(false);
    }
  };
  useEffect(() => {
    window.addEventListener('popstate', clearSession);
    return () => {
      window.removeEventListener('popstate', clearSession);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentImageId]);

  useEffect(() => {
    const sessionId = localStorage.getItem('sessionId');
    const isRerun = location.state?.isRerun;
    
    // Don't push to history if it's a rerun (play button click)
    if (!sessionId && uploadedImage && currentImageId && !isRerun) {
      pushToHistory(uploadedImage, currentImageId);

      if (activeTool === 'Outpaint') {
        setIsOutpaintActive(true);
        setHasOutpaintAspect(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state?.id, location.state?.isRerun]);

  const setHdImage = async (imageId: string, fallbackUrl?: string) => {
    try {
      const hdUrl = await getHdImageUrl(imageId);
      setUploadedImage(hdUrl);
    } catch (error) {
      if (fallbackUrl) setUploadedImage(fallbackUrl);
    }
  };
  useEffect(() => {
    const imageIdFromState = location.state?.id;
    const imageUrlFromState = location.state?.image;
    const isRerun = location.state?.isRerun;

    // Don't load image if it's a rerun (play button click) - we only want to load settings, not the image
    if (isRerun) {
      // Clear any existing image when it's a rerun (play button click)
      setUploadedImage(null);
      setCurrentImageId(null);
      setCurrentItemId(null);
      setGeneratedImages([]); // Clear generated images when rerunning
      setIsUploaded(false); // Reset upload state
    } else if (imageIdFromState) {
      // Only load image if it's not a rerun (edit button click)
      // IMPORTANT: Clear previously generated images when loading a new image for editing
      // This ensures the correct image is shown, not previously generated ones from persistence
      setGeneratedImages([]);
      setCurrentItemId(null);
      
      // Set the current image ID immediately to ensure it's available for editing
      setCurrentImageId(imageIdFromState);
      
      // Load the HD image URL
      setHdImage(imageIdFromState, imageUrlFromState);
      
      // Scroll to paint area when image is loaded (edit button click from same page)
      setTimeout(() => {
        const canvasArea = document.querySelector('.canvas-main-area') as HTMLElement;
        if (canvasArea) {
          const elementTop = canvasArea.getBoundingClientRect().top;
          const offsetPosition = window.scrollY + elementTop - 100; // 100px offset from top
          window.scrollTo({
            top: Math.max(0, offsetPosition),
            behavior: 'smooth'
          });
        } else {
          // Fallback: scroll to top of page
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }
      }, 300); // Slightly longer delay to ensure image is loaded
    }
  }, [location.state?.id, location.state?.image, location.state?.isRerun, location.state?.timestamp, location.state?.fromEditIcon, location.key]); // Add location.key to detect same-route navigation changes

  // Update prompt from navigation state when it changes
  useEffect(() => {
    // Always update prompt when state changes (including clearing if not present)
    // Handle both cases: when there's an id (image loaded) or when it's a rerun (play button)
    if (location.state?.id || location.state?.isRerun) {
      if (location.state?.prompt !== undefined) {
        setEditImagePrompt(location.state.prompt);
      }
    }
  }, [location, location.state?.prompt, location.state?.id, location.state?.isRerun, location.state?.timestamp]);

  // Fetch prompt from API if it's empty when image is loaded (skip for uploaded images - they are not in the images registry)
  useEffect(() => {
    const fetchPromptIfEmpty = async () => {
      if (!currentImageId || location.state?.prompt || isUploaded) return;
      try {
        const { data } = await imageGenerationClient.get(`/images/${currentImageId}`);
        if (data?.prompt && data.prompt.trim() !== '') {
          setEditImagePrompt(prev => {
            if (!prev || prev.trim() === '') return data.prompt;
            return prev;
          });
        }
      } catch (error) {
        // Silently fail - prompt might not be available
        console.error('Error fetching prompt:', error);
      }
    };

    fetchPromptIfEmpty();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentImageId, location.state?.prompt, isUploaded]);

  const handleDeleteImage = async () => {
    if (!currentImageId) {
      console.error('No image ID available to delete.');
      setUploadedImage(null);
      return;
    }

    try {
      !isUploaded ? await editorImageclient.deleteImage(currentImageId) : await editorImageclient.deleteImageUpload(currentImageId);
      setUploadedImage(null);
      setCurrentImageId(null);
      showSnackbar('Image deleted successfully!', 'success');
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || 'Something went wrong while deleting the image.';
      showSnackbar(errorMessage, 'error');
      console.error('Failed to delete image:', error);
    }
  };

  const handleToolAction = async (newImage: { id: string; url: string }) => {
    // Switch to Edit mode when using editing tools
    switchToEditMode();
    // Clear all generated images immediately when edit starts
    setGeneratedImages([]);
    if (newImage?.id && newImage?.url) {
      // Get HD image URL
      const hdUrl = await getHdImageUrl(newImage.id);
      
      // Construct a GenerateImageResponse from the newImage
      const updatedData: GenerateImageResponse = {
        images: [{
          id: newImage.id,
          url: hdUrl,
          artStyleId: undefined,
          imageCategories: imageCategories,
          projectId: projectId || undefined
        }]
      };
      
      // When editing, clear all other generated images and show only the edited image
      setGeneratedImages([updatedData]);
      
      // Update current image state
      setUploadedImage(hdUrl);
      setCurrentImageId(newImage.id);
      pushToHistory(hdUrl, newImage.id);
      
      // Update image size for display
      const newImageSize = await getImageSize(hdUrl);
      setImageSize(newImageSize);
      
      // Update canvas dimensions to fit the image
      const mainArea = document.querySelector('.canvas-main-area');
      if (mainArea) {
        const containerWidth = mainArea.clientWidth * 0.95;
        const containerHeight = STATIC_PAINT_AREA_HEIGHT;
        const imageRatio = newImageSize.width / newImageSize.height;
        let displayWidth, displayHeight;
        if (containerWidth / containerHeight > imageRatio) {
          displayHeight = containerHeight;
          displayWidth = displayHeight * imageRatio;
        } else {
          displayWidth = containerWidth;
          displayHeight = displayWidth / imageRatio;
        }
        setCanvasDimensions({ width: containerWidth, height: containerHeight });
        setImageBox({
          x: (containerWidth - displayWidth) / 2,
          y: (containerHeight - displayHeight) / 2,
          width: displayWidth,
          height: displayHeight
        });
      }
    }
  };

  const getImageSize = (urlOrFile: string | File): Promise<{ width: number; height: number }> => {
    return new Promise((resolve, reject) => {
      const img = new Image();

      img.onload = () => {
        resolve({ width: img.width, height: img.height });
      };

      img.onerror = reject;

      if (typeof urlOrFile === 'string') {
        img.src = urlOrFile;
      } else {
        img.src = URL.createObjectURL(urlOrFile);
      }
    });
  };

  const [HW, setHW] = useState<{
    height: number;
    width: number;
  }>({
    height: 0,
    width: 0
  });

  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      const file = acceptedFiles[0];
      if (!file) return;

      const res = await getImageSize(file);
      setHW({ ...HW, height: res?.height, width: res?.width });

      // ADD THIS: Force image size update immediately
      setImageSize(res);

      const reader = new FileReader();
      reader.onload = () => {
        const imageDataUrl = reader.result as string;
        setPassedId(imageDataUrl);
      };
      reader.readAsDataURL(file);

      try {
        setIsGenerating(true);

        const { data: uploadData } = await editorImageclient.uploadImages(file);
        const uploadedImageUrl = uploadData?.url;
        const uploadedImageId = uploadData?.id;

        if (!uploadedImageUrl || !uploadedImageId) {
          showSnackbar('Failed to upload image.', 'error');
          return;
        }

        setIsUploaded(true);
        setUploadedImage(uploadedImageUrl);
        setCurrentImageId(uploadedImageId);
        setPassedId(uploadedImageId);

        // Switch to Generate mode when image is uploaded
        setCanvaSettings(settings => {
          if (settings.model !== DEFAULT_GENERATE_MODEL) {
            return {
              ...settings,
              model: DEFAULT_GENERATE_MODEL
            };
          }
          return settings;
        });

        if (activeTool === 'Outpaint') {
          setIsOutpaintActive(true);
          setHasOutpaintAspect(true);
        }

        // ADD THIS: Ensure image bounds are calculated after upload
        setTimeout(() => {
          const mainArea = document.querySelector('.canvas-main-area');
          if (mainArea && res) {
            const containerWidth = mainArea.clientWidth * 0.95;
            const containerHeight = STATIC_PAINT_AREA_HEIGHT;

            const imageAspectRatio = res.width / res.height;
            const containerAspectRatio = containerWidth / containerHeight;
            let displayWidth, displayHeight;

            if (imageAspectRatio > containerAspectRatio) {
              displayWidth = containerWidth;
              displayHeight = displayWidth / imageAspectRatio;
            } else {
              displayHeight = containerHeight;
              displayWidth = displayHeight * imageAspectRatio;
            }

            setCanvasDimensions({ width: containerWidth, height: containerHeight });
            setImageBox({
              x: (containerWidth - displayWidth) / 2,
              y: (containerHeight - displayHeight) / 2,
              width: displayWidth,
              height: displayHeight
            });
          }
        }, 100);

        pushToHistory(uploadedImageUrl, uploadedImageId);
      } catch (error: any) {
        // Handle error
      } finally {
        setIsGenerating(false);
      }
    },
    [pushToHistory, HW, showSnackbar, activeTool, setCanvaSettings]
  );

  // Helper function to check if a URL is a video
  const isVideoUrl = (url: string | null | undefined): boolean => {
    if (!url) return false;
    return url.toLowerCase().endsWith('.mp4') || 
           url.toLowerCase().endsWith('.webm') || 
           url.toLowerCase().endsWith('.mov') ||
           url.toLowerCase().includes('/videos/');
  };

  // Restore image/video when switching between image and video modes
  useEffect(() => {
    if (!props.contentTypeTab) return;
    
    // Calculate total generated content count
    const totalGeneratedContent = generatedImages.reduce((total, response) => 
      total + (response?.images?.length || 0), 0
    );
    
    // Don't restore uploadedImage if there are multiple images - we want to show the stacked view
    if (totalGeneratedContent > 1) {
      // Keep uploadedImage as null to show stacked view
      if (uploadedImage) {
        setUploadedImage(null);
        setCurrentImageId(null);
      }
      return;
    }
    
    // Check if current uploadedImage matches the current mode
    const uploadedImageMatchesMode = uploadedImage ? 
      (props.contentTypeTab === 'video' ? isVideoUrl(uploadedImage) : !isVideoUrl(uploadedImage)) : 
      false;
    
    // If uploadedImage doesn't match the current mode, restore from generatedImages (only for single image)
    if (!uploadedImageMatchesMode && generatedImages.length > 0 && totalGeneratedContent === 1) {
      // Find the first content that matches the current mode
      for (const response of generatedImages) {
        if (!response?.images || response.images.length === 0) continue;
        
        for (const item of response.images) {
          const itemIsVideo = isVideoUrl(item.url);
          const matchesMode = props.contentTypeTab === 'video' ? itemIsVideo : !itemIsVideo;
          
          if (matchesMode && item.url && item.id) {
            // Restore the first matching content (only for single image)
            setUploadedImage(item.url);
            setCurrentImageId(item.id);
            pushToHistory(item.url, item.id);
            if (item.imageCategories) {
              setImageCategories(item.imageCategories);
            }
            return; // Exit after restoring the first match
          }
        }
      }
      
      // If no matching content found, clear uploadedImage and currentImageId
      if (!uploadedImageMatchesMode) {
        setUploadedImage(null);
        setCurrentImageId(null);
      }
    }
  }, [props.contentTypeTab, generatedImages, uploadedImage, setImageCategories, pushToHistory]);

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: {
      'image/jpeg': ['.jpeg', '.jpg'],
      'image/png': ['.png']
    },
    maxFiles: 10,
    disabled: isGenerating
  });
  const download = async () => {
    if (!currentImageId && !uploadedImage) return; // Nothing to download

    setIsDownloading(true); // Set loading state to true
    try {
      let downloadUrl = uploadedImage; // Default to the thumbnail

      if (currentImageId) {
        // Fetch the HD image URL if an ID is available
        const hdUrl = await getHdImageUrl(currentImageId);
        downloadUrl = hdUrl;
      }

      if (downloadUrl) {
        const link = document.createElement('a');
        link.href = downloadUrl;
        // Use a more descriptive name for the downloaded file
        link.download = `image-${currentImageId || 'download'}.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || 'Failed to download HD image.';
      showSnackbar(errorMessage, 'error');
      console.error('Download error:', error);
    } finally {
      setIsDownloading(false); // Reset loading state
    }
  };

  const iconList = [
    { Icon: RemoveBg, title: 'Remove Bg', isMultiStep: false },
    { Icon: ReplaceBg, title: 'Replace Bg', isMultiStep: true },
    { Icon: Erase, title: 'Erase', isMultiStep: true },
    { Icon: inpaint, title: 'Inpaint', isMultiStep: true },
    { Icon: outpaint, title: 'Outpaint', isMultiStep: true },
    { Icon: upscale, title: 'Upscale', isMultiStep: false },
    { Icon: deblur, title: 'Deblur', isMultiStep: false },
    { Icon: denoise, title: 'Denoise', isMultiStep: false },
    { Icon: retouch, title: 'Retouch', isMultiStep: false }
  ];

  const toolComponentMap: Record<string, React.ComponentType<ToolComponentProps>> = {
    'Replace Bg': ReplaceBgTool,
    Inpaint: InpaintTool,
    Erase: EraseTool,
    Outpaint: OutpaintTool
  };
  const handleAspectRatioChange = useCallback(
    (newRatioValue: string) => {
      setSelectedAspectRatio(newRatioValue);
      setIsOutpaintFrameLocked(false);
      setHasOutpaintAspect(true);

      if (!imageSize.width) return;
      const mainArea = document.querySelector('.canvas-main-area');
      if (!mainArea) return;

      const containerWidth = mainArea.clientWidth * 0.95;
      const containerHeight = STATIC_PAINT_AREA_HEIGHT;

      let targetRatio;
      if (newRatioValue === 'original') {
        targetRatio = imageSize.width / imageSize.height;
      } else if (newRatioValue === 'custom') {
        // For custom, we wait for user input from the other handler.
        return;
      } else {
        const parts = newRatioValue.split(':').map(Number);
        if (parts.length !== 2 || isNaN(parts[0]) || isNaN(parts[1])) return;
        targetRatio = parts[0] / parts[1];
      }

      // This part calculates the on-screen PREVIEW size
      let newCanvasWidth, newCanvasHeight;
      const containerRatio = containerWidth / containerHeight;

      if (containerRatio > targetRatio) {
        newCanvasHeight = containerHeight;
        newCanvasWidth = newCanvasHeight * targetRatio;
      } else {
        newCanvasWidth = containerWidth;
        newCanvasHeight = newCanvasWidth / targetRatio;
      }

      setCanvasDimensions({ width: newCanvasWidth, height: newCanvasHeight });

      // Recalculate and center the inner image box
      const imageRatio = imageSize.width / imageSize.height;
      let newImageDisplayWidth, newImageDisplayHeight;
      if (newCanvasWidth / newCanvasHeight > imageRatio) {
        newImageDisplayHeight = newCanvasHeight;
        newImageDisplayWidth = newImageDisplayHeight * imageRatio;
      } else {
        newImageDisplayWidth = newCanvasWidth;
        newImageDisplayHeight = newImageDisplayWidth / imageRatio;
      }

      setImageBox({
        width: newImageDisplayWidth,
        height: newImageDisplayHeight,
        x: (newCanvasWidth - newImageDisplayWidth) / 2,
        y: (newCanvasHeight - newImageDisplayHeight) / 2
      });
    },
    [imageSize]
  );

  const handleDimensionsChange = useCallback(
    (newDimensions: { width: number; height: number }) => {
      setIsOutpaintFrameLocked(false);
      setHasOutpaintAspect(true);
      setSelectedAspectRatio('custom');
      setGenerationDimensions(newDimensions);

      if (!imageSize.width || !newDimensions.width || !newDimensions.height) return;
      const mainArea = document.querySelector('.canvas-main-area');
      if (!mainArea) return;

      // This logic is identical to handleAspectRatioChange, but uses the custom dimensions' ratio
      const containerWidth = mainArea.clientWidth * 0.95;
      const containerHeight = STATIC_PAINT_AREA_HEIGHT;
      const targetRatio = newDimensions.width / newDimensions.height;

      let newCanvasWidth, newCanvasHeight;
      const containerRatio = containerWidth / containerHeight;

      if (containerRatio > targetRatio) {
        newCanvasHeight = containerHeight;
        newCanvasWidth = newCanvasHeight * targetRatio;
      } else {
        newCanvasWidth = containerWidth;
        newCanvasHeight = newCanvasWidth / targetRatio;
      }

      setCanvasDimensions({ width: newCanvasWidth, height: newCanvasHeight });

      const imageRatio = imageSize.width / imageSize.height;
      let newImageDisplayWidth, newImageDisplayHeight;

      if (newCanvasWidth / newCanvasHeight > imageRatio) {
        newImageDisplayHeight = newCanvasHeight;
        newImageDisplayWidth = newImageDisplayHeight * imageRatio;
      } else {
        newImageDisplayWidth = newCanvasWidth;
        newImageDisplayHeight = newImageDisplayWidth / imageRatio;
      }

      setImageBox({
        width: newImageDisplayWidth,
        height: newImageDisplayHeight,
        x: (newCanvasWidth - newImageDisplayWidth) / 2,
        y: (newCanvasHeight - newImageDisplayHeight) / 2
      });
    },
    [imageSize]
  );
  useEffect(() => {
    if (uploadedImage && !imageSize.width) {
      getImageSize(uploadedImage).then(size => {
        setImageSize(size);

        const mainArea = document.querySelector('.canvas-main-area');
        let finalCanvasWidth, finalCanvasHeight;

        if (mainArea) {
          const containerWidth = mainArea.clientWidth * 0.95;
          const containerHeight = STATIC_PAINT_AREA_HEIGHT;

          if (isOutpaintActive) {
            const imageAspectRatio = size.width / size.height;
            const containerAspectRatio = containerWidth / containerHeight;
            let displayWidth, displayHeight;
            if (imageAspectRatio > containerAspectRatio) {
              displayWidth = containerWidth;
              displayHeight = displayWidth / imageAspectRatio;
            } else {
              displayHeight = containerHeight;
              displayWidth = displayHeight * imageAspectRatio;
            }
            finalCanvasWidth = containerWidth;
            finalCanvasHeight = containerHeight;
            setCanvasDimensions({ width: finalCanvasWidth, height: finalCanvasHeight });
            setImageBox({
              x: (finalCanvasWidth - displayWidth) / 2,
              y: (finalCanvasHeight - displayHeight) / 2,
              width: displayWidth,
              height: displayHeight
            });
            return;
          }
          finalCanvasWidth = containerWidth;
          finalCanvasHeight = containerHeight;
          setCanvasDimensions({ width: finalCanvasWidth, height: finalCanvasHeight });
        } else {
          finalCanvasWidth = 800;
          finalCanvasHeight = 600;
          setCanvasDimensions({ width: finalCanvasWidth, height: finalCanvasHeight });
        }

        const imageWidth = Math.min(size.width, finalCanvasWidth);
        const imageHeight = Math.min(size.height, finalCanvasHeight);

        setImageBox({
          x: (finalCanvasWidth - imageWidth) / 2,
          y: (finalCanvasHeight - imageHeight) / 2,
          width: imageWidth,
          height: imageHeight
        });
      });
    }
  }, [uploadedImage, imageSize.width, isOutpaintActive]);

  useEffect(() => {
    if (!isOutpaintActive && imageSize.width) {
      setCanvasDimensions({ width: imageSize.width, height: imageSize.height });
      setImageBox({ x: 0, y: 0, width: imageSize.width, height: imageSize.height });
    }
  }, [isOutpaintActive, imageSize.width, imageSize.height]);

  useEffect(() => {
    if (activeTool === 'Outpaint') {
      setIsOutpaintActive(true);
    }
  }, [activeTool]);

  useEffect(() => {
    if (!isOutpaintActive || !imageSize.width) return;

    const mainArea = document.querySelector('.canvas-main-area') as HTMLElement | null;
    if (!mainArea) return;

    const containerWidth = mainArea.clientWidth * 0.95;
    const containerHeight = STATIC_PAINT_AREA_HEIGHT;

    if (!hasOutpaintEverGenerated) {
      // Initial entry into Outpaint: enforce 16:9 checkerboard
      const targetRatio = 16 / 9;
      let canvasWidth = containerWidth;
      let canvasHeight = canvasWidth / targetRatio;
      if (canvasHeight > containerHeight) {
        canvasHeight = containerHeight;
        canvasWidth = canvasHeight * targetRatio;
      }

      setCanvasDimensions({ width: canvasWidth, height: canvasHeight });

      // Fit the existing image inside the 16:9 canvas
      const imageRatio = imageSize.width / imageSize.height;
      let imgDisplayWidth = canvasWidth;
      let imgDisplayHeight = imgDisplayWidth / imageRatio;
      if (imgDisplayHeight > canvasHeight) {
        imgDisplayHeight = canvasHeight;
        imgDisplayWidth = imgDisplayHeight * imageRatio;
      }

      setImageBox({
        x: (canvasWidth - imgDisplayWidth) / 2,
        y: (canvasHeight - imgDisplayHeight) / 2,
        width: imgDisplayWidth,
        height: imgDisplayHeight
      });
      return;
    }

    // After outpaint generation: match checkerboard to the new image aspect ratio
    const imageRatio = imageSize.width / imageSize.height;
    let displayHeight = containerHeight;
    let displayWidth = displayHeight * imageRatio;
    if (displayWidth > containerWidth) {
      displayWidth = containerWidth;
      displayHeight = displayWidth / imageRatio;
    }

    setCanvasDimensions({ width: displayWidth, height: displayHeight });
    setImageBox({ x: 0, y: 0, width: displayWidth, height: displayHeight });
  }, [isOutpaintActive, imageSize, hasOutpaintEverGenerated]);

  const dragInfoRef = useRef({
    isResizing: false,
    isDragging: false,
    handle: null as string | null,
    startX: 0,
    startY: 0,
    startBox: null as any
  });

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      const { isResizing, isDragging, startX, startY, startBox, handle } = dragInfoRef.current;
      if (!isResizing && !isDragging) return;
      e.preventDefault();

      const dx = e.clientX - startX;
      const dy = e.clientY - startY;

      if (isResizing) {
        const imageRatio = imageSize.width / imageSize.height;
        let newWidth = startBox.width;
        let newHeight = startBox.height;
        let newX = startBox.x;
        let newY = startBox.y;

        if (handle?.includes('r')) newWidth = startBox.width + dx;
        if (handle?.includes('l')) {
          newWidth = startBox.width - dx;
          newX = startBox.x + dx;
        }
        if (handle?.includes('b')) newHeight = startBox.height + dy;
        if (handle?.includes('t')) {
          newHeight = startBox.height - dy;
          newY = startBox.y + dy;
        }

        if (handle && handle.length === 2) {
          if (Math.abs(dx) > Math.abs(dy)) {
            newHeight = newWidth / imageRatio;
          } else {
            newWidth = newHeight * imageRatio;
          }
        }

        if (handle === 'tl') {
          newX = startBox.x + (startBox.width - newWidth);
          newY = startBox.y + (startBox.height - newHeight);
        }
        if (handle === 'tr') {
          newY = startBox.y + (startBox.height - newHeight);
        }
        if (handle === 'bl') {
          newX = startBox.x + (startBox.width - newWidth);
        }

        if (newWidth < 50 || newHeight < 50) return;
        setImageBox({ width: newWidth, height: newHeight, x: newX, y: newY });
      } else if (isDragging) {
        let newX = startBox.x + dx;
        let newY = startBox.y + dy;

        if (newX < 0) newX = 0;
        if (newY < 0) newY = 0;
        if (newX + startBox.width > canvasDimensions.width) newX = canvasDimensions.width - startBox.width;
        if (newY + startBox.height > canvasDimensions.height) newY = canvasDimensions.height - startBox.height;

        setImageBox({ ...startBox, x: newX, y: newY });
      }
    };

    const handleMouseUp = () => {
      dragInfoRef.current.isResizing = false;
      dragInfoRef.current.isDragging = false;
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [imageSize, canvasDimensions]);

  const handleMouseDown = (e: React.MouseEvent, type: 'resize' | 'drag', handle?: string) => {
    if (isOutpaintFrameLocked) return;
    e.preventDefault();
    e.stopPropagation();
    dragInfoRef.current = {
      isResizing: type === 'resize',
      isDragging: type === 'drag',
      handle,
      startX: e.clientX,
      startY: e.clientY,
      startBox: imageBox
    };
  };

  const removeBackground = async () => {
    if (!uploadedImage || !canvasApiRef.current) return;

    // Switch to Edit mode when starting to edit
    switchToEditMode();
    // Clear all generated images immediately BEFORE starting the edit operation
    setGeneratedImages([]);
    setIsGenerating(true);
    try {
      await canvasApiRef.current.executeImageGeneration(
        async request => ({
          ...request,
          prompt: editImagePrompt || '',
          subject: currentImageId,
          imageCategories: imageCategories,
          ...(imageCategories.includes('persona') && projectId && { projectId })
        }),
        async (client, request) => {
          try {
            const { data }: { data: GenerateImageResponse } = await client.removeSubjectBackground(request);
            const newImage = data?.images?.[0];
            console.log('newImage', newImage);

            if (newImage?.url && newImage?.id) {
              // Get HD image URL and set it
              const hdUrl = await getHdImageUrl(newImage.id);
              
              // Update the response data with HD URL before adding to array
              const updatedData: GenerateImageResponse = {
                ...data,
                images: data.images.map(img => 
                  img.id === newImage.id 
                    ? { ...img, url: hdUrl }
                    : img
                )
              };
              
              // When editing, clear all other generated images and show only the edited image
              setGeneratedImages([updatedData]);
              
              // Update current image state
              setUploadedImage(hdUrl);
              setCurrentImageId(newImage.id);
              pushToHistory(hdUrl, newImage.id);
              setIsOutpaintFrameLocked(true);
              
              // Update image size for display
              const newImageSize = await getImageSize(hdUrl);
              setImageSize(newImageSize);
              
              // Update canvas dimensions to fit the image
              const mainArea = document.querySelector('.canvas-main-area');
              if (mainArea) {
                const containerWidth = mainArea.clientWidth * 0.95;
                const containerHeight = STATIC_PAINT_AREA_HEIGHT;
                const imageRatio = newImageSize.width / newImageSize.height;
                let displayWidth, displayHeight;
                if (containerWidth / containerHeight > imageRatio) {
                  displayHeight = containerHeight;
                  displayWidth = displayHeight * imageRatio;
                } else {
                  displayWidth = containerWidth;
                  displayHeight = displayWidth / imageRatio;
                }
                setCanvasDimensions({ width: containerWidth, height: containerHeight });
                setImageBox({
                  x: (containerWidth - displayWidth) / 2,
                  y: (containerHeight - displayHeight) / 2,
                  width: displayWidth,
                  height: displayHeight
                });
              }
            }
            return data;
          } catch (error: any) {
            const errorMessage =
              error?.response?.data?.errors?.[0]?.message ||
              error?.message ||
              error?.response?.data?.message ||
              'Something went wrong while removing the background.';

            showSnackbar(errorMessage, 'error');
          }
        }
      );
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.errors?.[0]?.message ||
        error?.message ||
        error?.response?.data?.message ||
        'Something went wrong while removing the background.';

      showSnackbar(errorMessage, 'error');
    } finally {
      setIsGenerating(false);
      const removeBgTool = iconList.find(tool => tool.title === 'Remove Bg');
      if (!removeBgTool?.isMultiStep) {
        setActiveTool("Generate");
      }
    }
  };

  const upscaleImage = async () => {
    if (!uploadedImage || !canvasApiRef.current) return;

    // Switch to Edit mode when starting to edit
    switchToEditMode();
    // Clear all generated images immediately BEFORE starting the edit operation
    setGeneratedImages([]);
    setIsGenerating(true);
    try {
      await canvasApiRef.current.executeImageGeneration(
        async request => ({
          ...request,
          prompt: editImagePrompt || '',
          subject: currentImageId || '',
          imageCategories: imageCategories,
          ...(imageCategories.includes('persona') && projectId && { projectId })
        }),
        async (client, request) => {
          try {
            const { data }: { data: GenerateImageResponse } = await client.upscaleSubject(request);
            const newImage = data?.images?.[0];

            if (newImage?.url && newImage?.id) {
              // Get HD image URL and set it
              const hdUrl = await getHdImageUrl(newImage.id);
              
              // Update the response data with HD URL before adding to array
              const updatedData: GenerateImageResponse = {
                ...data,
                images: data.images.map(img => 
                  img.id === newImage.id 
                    ? { ...img, url: hdUrl }
                    : img
                )
              };
              
              // When editing, clear all other generated images and show only the edited image
              setGeneratedImages([updatedData]);
              
              // Update current image state
              setUploadedImage(hdUrl);
              setCurrentImageId(newImage.id);
              pushToHistory(hdUrl, newImage.id);

              if (newImage.imageCategories) {
                setImageCategories(newImage.imageCategories);
              }
              
              // Update image size for display
              const newImageSize = await getImageSize(hdUrl);
              setImageSize(newImageSize);
              
              // Update canvas dimensions to fit the image
              const mainArea = document.querySelector('.canvas-main-area');
              if (mainArea) {
                const containerWidth = mainArea.clientWidth * 0.95;
                const containerHeight = STATIC_PAINT_AREA_HEIGHT;
                const imageRatio = newImageSize.width / newImageSize.height;
                let displayWidth, displayHeight;
                if (containerWidth / containerHeight > imageRatio) {
                  displayHeight = containerHeight;
                  displayWidth = displayHeight * imageRatio;
                } else {
                  displayWidth = containerWidth;
                  displayHeight = displayWidth / imageRatio;
                }
                setCanvasDimensions({ width: containerWidth, height: containerHeight });
                setImageBox({
                  x: (containerWidth - displayWidth) / 2,
                  y: (containerHeight - displayHeight) / 2,
                  width: displayWidth,
                  height: displayHeight
                });
              }
            }
            return data;
          } catch (error: any) {
            const errorMessage =
              error?.response?.data?.errors?.[0]?.message ||
              error?.message ||
              error?.response?.data?.message ||
              'Something went wrong while upscaling the image.';

            showSnackbar(errorMessage, 'error');
          }
        }
      );
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.errors?.[0]?.message || error?.message || error?.response?.data?.message || 'Something went wrong while upscaling the image.';

      showSnackbar(errorMessage, 'error');
    } finally {
      setIsGenerating(false);
      const upscaleTool = iconList.find(tool => tool.title === 'Upscale');
      if (!upscaleTool?.isMultiStep) {
        setActiveTool("Generate");
      }
    }
  };

  const deblurImage = async () => {
    if (!uploadedImage || !canvasApiRef.current) return;

    // Switch to Edit mode when starting to edit
    switchToEditMode();
    // Clear all generated images immediately BEFORE starting the edit operation
    setGeneratedImages([]);
    setIsGenerating(true);
    try {
      await canvasApiRef.current.executeImageGeneration(
        async request => ({
          ...request,
          prompt: editImagePrompt || '',
          subject: currentImageId || '',
          imageCategories: imageCategories,
          ...(imageCategories.includes('persona') && projectId && { projectId })
        }),
        async (client, request) => {
          try {
            const { data }: { data: GenerateImageResponse } = await client.deblurSubject(request);
            const newImage = data?.images?.[0];

            if (newImage?.url && newImage?.id) {
              // Get HD image URL and set it
              const hdUrl = await getHdImageUrl(newImage.id);
              
              // Update the response data with HD URL before adding to array
              const updatedData: GenerateImageResponse = {
                ...data,
                images: data.images.map(img => 
                  img.id === newImage.id 
                    ? { ...img, url: hdUrl }
                    : img
                )
              };
              
              // When editing, clear all other generated images and show only the edited image
              setGeneratedImages([updatedData]);
              
              // Update current image state
              setUploadedImage(hdUrl);
              setCurrentImageId(newImage.id);
              pushToHistory(hdUrl, newImage.id);
              
              if (newImage.imageCategories) {
                setImageCategories(newImage.imageCategories);
              }
              
              // Update image size for display
              const newImageSize = await getImageSize(hdUrl);
              setImageSize(newImageSize);
              
              // Update canvas dimensions to fit the image
              const mainArea = document.querySelector('.canvas-main-area');
              if (mainArea) {
                const containerWidth = mainArea.clientWidth * 0.95;
                const containerHeight = STATIC_PAINT_AREA_HEIGHT;
                const imageRatio = newImageSize.width / newImageSize.height;
                let displayWidth, displayHeight;
                if (containerWidth / containerHeight > imageRatio) {
                  displayHeight = containerHeight;
                  displayWidth = displayHeight * imageRatio;
                } else {
                  displayWidth = containerWidth;
                  displayHeight = displayWidth / imageRatio;
                }
                setCanvasDimensions({ width: containerWidth, height: containerHeight });
                setImageBox({
                  x: (containerWidth - displayWidth) / 2,
                  y: (containerHeight - displayHeight) / 2,
                  width: displayWidth,
                  height: displayHeight
                });
              }
            }
            return data;
          } catch (error: any) {
            const errorMessage =
              error?.response?.data?.errors?.[0]?.message ||
              error?.message ||
              error?.response?.data?.message ||
              'Something went wrong while deblurring the image.';
            showSnackbar(errorMessage, 'error');
          }
        }
      );
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.errors?.[0]?.message || error?.message || error?.response?.data?.message || 'Something went wrong while deblurring the image.';
      showSnackbar(errorMessage, 'error');
    } finally {
      setIsGenerating(false);
      const deblurTool = iconList.find(tool => tool.title === 'Deblur');
      if (!deblurTool?.isMultiStep) {
        setActiveTool("Generate");
      }
    }
  };

  const denoiseImage = async () => {
    if (!uploadedImage || !canvasApiRef.current) return;

    // Switch to Edit mode when starting to edit
    switchToEditMode();
    // Clear all generated images immediately BEFORE starting the edit operation
    setGeneratedImages([]);
    setIsGenerating(true);
    try {
      await canvasApiRef.current.executeImageGeneration(
        async request => ({
          ...request,
          prompt: editImagePrompt || '',
          subject: currentImageId || '',
          imageCategories: imageCategories,
          ...(imageCategories.includes('persona') && projectId && { projectId })
        }),
        async (client, request) => {
          try {
            const { data }: { data: GenerateImageResponse } = await client.denoiseSubject(request);
            const newImage = data?.images?.[0];

            if (newImage?.url && newImage?.id) {
              // Get HD image URL and set it
              const hdUrl = await getHdImageUrl(newImage.id);
              
              // Update the response data with HD URL before adding to array
              const updatedData: GenerateImageResponse = {
                ...data,
                images: data.images.map(img => 
                  img.id === newImage.id 
                    ? { ...img, url: hdUrl }
                    : img
                )
              };
              
              // When editing, clear all other generated images and show only the edited image
              setGeneratedImages([updatedData]);
              
              // Update current image state
              setUploadedImage(hdUrl);
              setCurrentImageId(newImage.id);
              pushToHistory(hdUrl, newImage.id);
              
              if (newImage.imageCategories) {
                setImageCategories(newImage.imageCategories);
              }
              
              // Update image size for display
              const newImageSize = await getImageSize(hdUrl);
              setImageSize(newImageSize);
              
              // Update canvas dimensions to fit the image
              const mainArea = document.querySelector('.canvas-main-area');
              if (mainArea) {
                const containerWidth = mainArea.clientWidth * 0.95;
                const containerHeight = STATIC_PAINT_AREA_HEIGHT;
                const imageRatio = newImageSize.width / newImageSize.height;
                let displayWidth, displayHeight;
                if (containerWidth / containerHeight > imageRatio) {
                  displayHeight = containerHeight;
                  displayWidth = displayHeight * imageRatio;
                } else {
                  displayWidth = containerWidth;
                  displayHeight = displayWidth / imageRatio;
                }
                setCanvasDimensions({ width: containerWidth, height: containerHeight });
                setImageBox({
                  x: (containerWidth - displayWidth) / 2,
                  y: (containerHeight - displayHeight) / 2,
                  width: displayWidth,
                  height: displayHeight
                });
              }
            }
            return data;
          } catch (error: any) {
            const errorMessage =
              error?.response?.data?.errors?.[0]?.message ||
              error?.message ||
              error?.response?.data?.message ||
              'Something went wrong while denoising the image.';
            showSnackbar(errorMessage, 'error');
            return { images: [] } as GenerateImageResponse; // Return empty response on error
          }
        }
      );
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.errors?.[0]?.message ||
        error?.message ||
        error?.response?.data?.message ||
        'Something went wrong while denoising the image.';
      showSnackbar(errorMessage, 'error');
    } finally {
      setIsGenerating(false);
      const denoiseTool = iconList.find(tool => tool.title === 'Denoise');
      if (!denoiseTool?.isMultiStep) {
        setActiveTool("Generate");
      }
    }
  };

  const retouchImage = async () => {
    if (!uploadedImage || !canvasApiRef.current) return;

    // Switch to Edit mode when starting to edit
    switchToEditMode();
    // Clear all generated images immediately BEFORE starting the edit operation
    setGeneratedImages([]);
    setIsGenerating(true);
    try {
      await canvasApiRef.current.executeImageGeneration(
        async request => ({
          ...request,
          prompt: editImagePrompt || '',
          subject: currentImageId || '',
          imageCategories: imageCategories,
          ...(imageCategories.includes('persona') && projectId && { projectId })
        }),
        async (client, request) => {
          try {
            const { data }: { data: GenerateImageResponse } = await client.retouchSubject(request);
            const newImage = data?.images?.[0];

            if (newImage?.url && newImage?.id) {
              // Check for both URL and ID
              setHdImage(newImage.id, newImage.url);
              pushToHistory(newImage.url, newImage.id);
              setCurrentImageId(newImage.id);
              if (newImage.imageCategories) {
                setImageCategories(newImage.imageCategories);
              }
            }
            return data;
          } catch (error: any) {
            const errorMessage =
              error?.response?.data?.errors?.[0]?.message ||
              error?.message ||
              error?.response?.data?.message ||
              'Something went wrong while retouching the image.';
            showSnackbar(errorMessage, 'error');
          }
        }
      );
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.errors?.[0]?.message || error?.message || error?.response?.data?.message || 'Something went wrong while retouching the image.';
      showSnackbar(errorMessage, 'error');
    } finally {
      setIsGenerating(false);
      const retouchTool = iconList.find(tool => tool.title === 'Retouch');
      if (!retouchTool?.isMultiStep) {
        setActiveTool("Generate");
      }
    }
  };
  // In CanvasAIEditor.tsx

  const handleOutpaintAction = async () => {
    if (!uploadedImage || !canvasApiRef.current || !currentImageId) return;
    
    // Switch to Edit mode when starting to edit
    switchToEditMode();
    // Clear all generated images immediately BEFORE starting the edit operation
    setGeneratedImages([]);
    setIsGenerating(true);

    const apiWidth = generationDimensions.width;
    const apiHeight = generationDimensions.height;

    const displayToApiScaleX = apiWidth / canvasDimensions.width;
    const displayToApiScaleY = apiHeight / canvasDimensions.height;

    const apiImageSize = [Math.round(imageBox.width * displayToApiScaleX), Math.round(imageBox.height * displayToApiScaleY)];
    const apiImageLocation = [Math.round(imageBox.x * displayToApiScaleX), Math.round(imageBox.y * displayToApiScaleY)];

    await canvasApiRef.current.executeImageGeneration(
      async request => ({
        ...request,
        prompt: ' ',
        subject: currentImageId,
        width: apiWidth,
        height: apiHeight,
        canvasSize: [apiWidth, apiHeight],
        originalImageSize: apiImageSize,
        originalImageLocation: apiImageLocation,
        imageCategories: imageCategories,
        ...(imageCategories.includes('persona') && projectId && { projectId })
      }),
      async (client, request) => {
        try {
          const { data } = await client.outpaintSubject(request);
          const newImage = data?.images?.[0];

          if (newImage?.url && newImage?.id) {
            // Get HD image URL
            const hdUrl = await getHdImageUrl(newImage.id);
            
            // Update the response data with HD URL before adding to array
            const updatedData: GenerateImageResponse = {
              ...data,
              images: data.images.map(img => 
                img.id === newImage.id 
                  ? { ...img, url: hdUrl }
                  : img
              )
            };
            
            // When editing, clear all other generated images and show only the edited image
            setGeneratedImages([updatedData]);
            
            // Update current image state
            const newImageSize = await getImageSize(hdUrl);
            setImageSize(newImageSize);
            setUploadedImage(hdUrl);
            setCurrentImageId(newImage.id);
            pushToHistory(hdUrl, newImage.id);
            setIsOutpaintFrameLocked(false);
            setHasOutpaintEverGenerated(true);
            
            if (newImage.imageCategories) {
              setImageCategories(newImage.imageCategories);
            }
            
            // Update canvas dimensions to fit the image
            const mainArea = document.querySelector('.canvas-main-area');
            if (mainArea) {
              const containerWidth = mainArea.clientWidth * 0.95;
              const containerHeight = STATIC_PAINT_AREA_HEIGHT;
              const imageRatio = newImageSize.width / newImageSize.height;
              let displayWidth, displayHeight;
              if (containerWidth / containerHeight > imageRatio) {
                displayHeight = containerHeight;
                displayWidth = displayHeight * imageRatio;
              } else {
                displayWidth = containerWidth;
                displayHeight = displayWidth / imageRatio;
              }
              setCanvasDimensions({ width: containerWidth, height: containerHeight });
              setImageBox({
                x: (containerWidth - displayWidth) / 2,
                y: (containerHeight - displayHeight) / 2,
                width: displayWidth,
                height: displayHeight
              });
            }
          }
          return data;
        } catch (error: any) {
          const errorMessage = error?.response?.data?.message || 'Something went wrong during outpainting.';
          showSnackbar(errorMessage, 'error');
        }
      }
    );

    setIsGenerating(false);
  };
  const replaceUsingPrompt = async () => {
    if (!canvasApiRef.current || !editImagePrompt.trim()) return;

    // Determine if we should edit or generate based on mode
    const currentMode = getModelCategory(canvaSettings.model);
    const shouldEdit = currentMode === 'Edit' && uploadedImage && currentImageId;
    
    // If editing, clear all generated images immediately BEFORE starting the edit operation
    if (shouldEdit) {
      setGeneratedImages([]);
    }
    
    setIsGenerating(true);
    try {
      
      // If there's an uploaded image and we're in Edit mode, edit it; otherwise generate a new image
      if (shouldEdit) {
        // Edit existing image
        await canvasApiRef.current?.executeImageGeneration(
          async requestData => ({
            ...requestData,
            prompt: editImagePrompt,
            subject: currentImageId || '',
            imageCategories: imageCategories,
          }),
          async (client, request) => {
            try {
              const { data }: { data: GenerateImageResponse } = await client.editSubjectWithText(request);
              const newImage = data?.images?.[0];

              if (newImage?.url && newImage?.id) {
                // Get HD image URL and set it
                const hdUrl = await getHdImageUrl(newImage.id);
                
                // Update the response data with HD URL before adding to array
                const updatedData: GenerateImageResponse = {
                  ...data,
                  images: data.images.map(img => 
                    img.id === newImage.id 
                      ? { ...img, url: hdUrl }
                      : img
                  )
                };
                
                // When editing, clear all other generated images and show only the edited image
                setGeneratedImages([updatedData]);
                
                // Then update the current image state
                setUploadedImage(hdUrl);
                setCurrentImageId(newImage.id);
                pushToHistory(hdUrl, newImage.id);

                if (newImage.imageCategories) {
                  setImageCategories(newImage.imageCategories);
                }

                // Update image size for display
                const newImageSize = await getImageSize(hdUrl);
                setImageSize(newImageSize);

                // Update canvas dimensions to fit the image
                const mainArea = document.querySelector('.canvas-main-area');
                if (mainArea) {
                  const containerWidth = mainArea.clientWidth * 0.95;
                  const containerHeight = STATIC_PAINT_AREA_HEIGHT;
                  const imageRatio = newImageSize.width / newImageSize.height;
                  let displayWidth, displayHeight;
                  if (containerWidth / containerHeight > imageRatio) {
                    displayHeight = containerHeight;
                    displayWidth = displayHeight * imageRatio;
                  } else {
                    displayWidth = containerWidth;
                    displayHeight = displayWidth / imageRatio;
                  }
                  setCanvasDimensions({ width: containerWidth, height: containerHeight });
                  setImageBox({
                    x: (containerWidth - displayWidth) / 2,
                    y: (containerHeight - displayHeight) / 2,
                    width: displayWidth,
                    height: displayHeight
                  });
                }
              }
              return data;
            } catch (error: any) {
              const errorMessage =
                error?.response?.data?.errors?.[0]?.message ||
                error?.message ||
                error?.response?.data?.message ||
                'Something went wrong while editing the image.';

              showSnackbar(errorMessage, 'error');
            }
          }
        );
      } else {
        // Check if we're generating video or image
        const isVideoGeneration = props.contentTypeTab === 'video';
        
        // Helper function to check if a URL is a video
        const isVideoUrl = (url: string | null | undefined): boolean => {
          if (!url) return false;
          return url.toLowerCase().endsWith('.mp4') || 
                 url.toLowerCase().endsWith('.webm') || 
                 url.toLowerCase().endsWith('.mov') ||
                 url.toLowerCase().includes('/videos/');
        };
        
        // Preserve opposite type: when generating images, keep videos; when generating videos, keep images
        if (isVideoGeneration) {
          // When generating video, preserve existing images (non-videos)
          setGeneratedImages(prev => prev.filter(response => 
            response.images?.some(img => !isVideoUrl(img.url)) ?? false
          ));
        } else {
          // When generating image, preserve existing videos
          setGeneratedImages(prev => prev.filter(response => 
            response.images?.some(img => isVideoUrl(img.url)) ?? false
          ));
        }
        
        setUploadedImage(null);
        setCurrentImageId(null);
        
        // Generate new image using sidebar settings
        const dimensions = canvaSettings.image_dimensions.split('x');
        const width = parseInt(dimensions[0], 10);
        const height = parseInt(dimensions[1], 10);
        
        // Validate dimensions
        if (isNaN(width) || isNaN(height) || width <= 0 || height <= 0) {
          showSnackbar('Invalid image dimensions. Please check your aspect ratio settings.', 'error');
          setIsGenerating(false);
          return;
        }

        // Validate custom dimensions (minimum 64x64, maximum 4096x4096)
        const aspectRatio = canvaSettings.aspectRatio;
        const isCustomAspectRatio = aspectRatio && aspectRatio.toLowerCase() === 'custom';
        
        if (isCustomAspectRatio) {
          const MIN_DIMENSION = 64;
          const MAX_DIMENSION = 4096;
          
          if (width < MIN_DIMENSION || height < MIN_DIMENSION) {
            showSnackbar('Please enter valid width and height.', 'warning');
            setIsGenerating(false);
            return;
          }
          
          if (width > MAX_DIMENSION || height > MAX_DIMENSION) {
            showSnackbar('Please enter valid width and height.', 'warning');
            setIsGenerating(false);
            return;
          }
        }

        const numImagesToGenerate = canvaSettings.number_of_images || 1;

        // Check if this is a LoRA model using modelType from API response
        const isLoRAModel = canvaSettings.modelType === 'text-to-image-lora';

        const request: GenerateImageRequest = {
          prompt: editImagePrompt,
          numImages: numImagesToGenerate,
          
          // For LoRA models, send modelId to identify the model (especially important for vanilla images)
          // For non-LoRA models, send modelId if it exists
          ...(canvaSettings.modelId ? { modelId: canvaSettings.modelId } : {}),
          
          // For LoRA models, also send model name so backend knows which base model was used
          ...(isLoRAModel && canvaSettings.modelName ? { modelName: canvaSettings.modelName } : {}),
          
          ...(canvaSettings.style ? { artStyleId: canvaSettings.style?.id } : {}),
          ...(canvaSettings.project ? { artStyleId: canvaSettings.project?.id } : {}),
          ...(canvaSettings.project ? { project_id: canvaSettings.project?.projectID } : {}),
          
          // Add dynamic parameters based on model
          // Resolution for Google Nano Banana Pro
          ...(canvaSettings.resolution ? { resolution: canvaSettings.resolution } : {}),
        };

        // For LoRA models, structure parameters according to API spec
        if (isLoRAModel) {
          // Initialize model_parameters for LoRA models
          request.model_parameters = request.model_parameters || {};
          
          if (canvaSettings.style?.id) {
            request.model_parameters.art_style_id = canvaSettings.style.id;
          } else if (canvaSettings.project?.id) {
            request.model_parameters.art_style_id = canvaSettings.project.id;
          }
          
          // Send num_images in model_parameters for LoRA models
          request.model_parameters.num_images = numImagesToGenerate;
        }

        // Handle aspect ratio and dimensions for ALL models
        // Always send aspect_ratio at top level (never send width/height at top level)
        // Explicitly remove width/height to ensure they're never sent at top level
        delete request.width;
        delete request.height;
        
        if (aspectRatio) {
          // Send aspect_ratio at top level for all models
          request.aspect_ratio = aspectRatio;
          
          // If Custom, include custom_image_size in model_parameters
          if (isCustomAspectRatio) {
            request.model_parameters = {
              ...(request.model_parameters || {}),
              custom_image_size: {
                width: width,
                height: height
              }
            };
          }
          
          // For LoRA models, also send image_size in model_parameters (mapped from feAspectRatioMapping)
          if (isLoRAModel && canvaSettings.feAspectRatioMapping) {
            const mappedImageSize = canvaSettings.feAspectRatioMapping[aspectRatio];
            if (mappedImageSize) {
              request.model_parameters = {
                ...(request.model_parameters || {}),
                image_size: mappedImageSize
              };
            }
          }
        } else {
          // If aspectRatio is not set, we still don't send width/height
          // Log a warning to help debug
          console.warn('aspectRatio is not set in canvaSettings. Please ensure aspect ratio is selected in the UI.');
        }
        // Never send width/height at top level for any model

        // Set image categories: use state when "Use Persona" (mypersona), else from settings
        if (imageCategories.includes('mypersona')) {
          request.imageCategories = imageCategories;
        } else if (canvaSettings.style) {
          request.imageCategories = ['art_style'];
        } else if (canvaSettings.project) {
          request.imageCategories = ['persona'];
        } else {
          request.imageCategories = ['vanilla'];
        }

        if (isVideoGeneration) {
          // Video generation logic
          if (!canvaSettings.videoModelId) {
            showSnackbar('Please select a video model first.', 'error');
            setIsGenerating(false);
            return;
          }
          
          // Preserve existing images when generating videos (already filtered above, but keep for consistency)
          // No need to clear again as we already filtered above
          setUploadedImage(null);
          setCurrentImageId(null);
          
          try {

            // Build video request using CanvasApi
            const videoRequest = canvasApiRef.current?.buildVideoRequest();

            if (!videoRequest) {
              showSnackbar('Failed to build video request. Please try again.', 'error');
              setIsGenerating(false);
              return;
            }

            // Override prompt with the current editImagePrompt
            videoRequest.prompt = editImagePrompt;

            // Add project/art style if available (but not imageCategories for videos)
            if (canvaSettings.style) {
              videoRequest.artStyleId = canvaSettings.style?.id;
            } else if (canvaSettings.project) {
              videoRequest.artStyleId = canvaSettings.project?.id;
              videoRequest.projectId = canvaSettings.project?.projectID;
            }
            // Post to /videos endpoint for video generation
            console.log('Video request payload:', videoRequest);
            const { data: videoData }: { data: any } = await imageGenerationClient.post('/videos', videoRequest);
            
            console.log('Video API Response:', videoData);
            
            // Handle video response (structure may differ from image response)
            let processedVideoData: GenerateImageResponse;
            if (videoData?.videos && Array.isArray(videoData.videos)) {
              // If response has videos array
              processedVideoData = {
                images: videoData.videos.map((vid: any) => ({
                  id: vid.id,
                  url: vid.url || vid.videoUrl,
                  artStyleId: vid.artStyleId,
                  imageCategories: vid.imageCategories || ['vanilla'],
                  projectId: vid.projectId,
                  width: vid.width,
                  height: vid.height
                }))
              };
            } else if (videoData?.video) {
              // If response is a single video object
              processedVideoData = {
                images: [{
                  id: videoData.video.id,
                  url: videoData.video.url || videoData.video.videoUrl,
                  artStyleId: videoData.video.artStyleId,
                  imageCategories: videoData.video.imageCategories || ['vanilla'],
                  projectId: videoData.video.projectId,
                  width: videoData.video.width,
                  height: videoData.video.height
                }]
              };
            } else if (Array.isArray(videoData)) {
              // If response is an array of videos directly
              processedVideoData = {
                images: videoData.map((vid: any) => ({
                  id: vid.id,
                  url: vid.url || vid.videoUrl,
                  artStyleId: vid.artStyleId,
                  imageCategories: vid.imageCategories || ['vanilla'],
                  projectId: vid.projectId,
                  width: vid.width,
                  height: vid.height
                }))
              };
            } else if (videoData?.images && Array.isArray(videoData.images)) {
              // If response has images array (videos might be returned as images)
              processedVideoData = {
                images: videoData.images.map((vid: any) => ({
                  id: vid.id,
                  url: vid.url || vid.videoUrl,
                  artStyleId: vid.artStyleId,
                  imageCategories: vid.imageCategories || ['vanilla'],
                  projectId: vid.projectId,
                  width: vid.width,
                  height: vid.height
                }))
              };
            } else {
              // Fallback: treat the entire response as a single video
              processedVideoData = {
                images: [{
                  id: videoData.id,
                  url: videoData.url || videoData.videoUrl,
                  artStyleId: videoData.artStyleId,
                  imageCategories: videoData.imageCategories || ['vanilla'],
                  projectId: videoData.projectId,
                  width: videoData.width,
                  height: videoData.height
                }]
              };
            }
            
            console.log('Processed video data:', processedVideoData);
            
            // Validate that we have videos
            if (!processedVideoData?.images || processedVideoData.images.length === 0) {
              console.error('No videos found in response:', videoData);
              showSnackbar('No videos were returned from the API. Please try again.', 'error');
              setIsGenerating(false);
              return;
            }
            
            // Fetch full video details (including prompt and modelName) for all generated videos
            const videoFallbackPrompt = videoRequest.prompt || prompt || '';
            const videoFallbackModelName = canvaSettings.videoModelName || '';
            
            const videosWithFullDetails = await Promise.all(
              processedVideoData.images.map(async (vid) => {
                const fullDetails = await fetchFullMediaDetails(
                  vid.id,
                  'video',
                  videoFallbackPrompt,
                  videoFallbackModelName
                );
                return {
                  ...vid,
                  prompt: fullDetails.prompt,
                  modelName: fullDetails.modelName
                };
              })
            );

            // Create updated response with full details
            const processedVideoDataWithDetails: GenerateImageResponse = {
              images: videosWithFullDetails
            };

            // Add the new response to the generated images array
            setGeneratedImages(prev => {
              const newArray = [...prev, processedVideoDataWithDetails];
              return newArray;
            });
            
            // Set the first video as the current one
            const firstVideo = processedVideoDataWithDetails.images[0];
            if (firstVideo?.url && firstVideo?.id) {
              setUploadedImage(firstVideo.url);
              setCurrentImageId(firstVideo.id);
              pushToHistory(firstVideo.url, firstVideo.id);
              
              if (firstVideo.imageCategories) {
                setImageCategories(firstVideo.imageCategories);
              }
            }
            
            showSnackbar('Video generated successfully!', 'success');
          } catch (error: any) {
            // Check for content policy violation (422 status or content_policy_violation type)
            const isContentPolicyViolation = 
              error?.response?.status === 422 || 
              error?.response?.data?.type === 'content_policy_violation';
            
            if (isContentPolicyViolation) {
              showSnackbar('The content could not be processed because it contained material flagged by a content checker.', 'error');
              setIsGenerating(false);
              return;
            }
            
            const errorMessage =
              error?.response?.data?.errors?.[0]?.message ||
              error?.message ||
              error?.response?.data?.message ||
              'Something went wrong while generating the video.';
            
            showSnackbar(errorMessage, 'error');
            console.error('Video generation error:', error);
          } finally {
            setIsGenerating(false);
          }
        } else {
          // Image generation logic (existing code)
          // Switch to Generate mode when generating new image
          switchToGenerateMode();
          
          // Preserve existing videos when generating images (already filtered above, but keep for consistency)
          // No need to clear again as we already filtered above
          setUploadedImage(null);
          setCurrentImageId(null);
          
          // Generate new image using sidebar settings
          const dimensions = canvaSettings.image_dimensions.split('x');
          const width = parseInt(dimensions[0], 10);
          const height = parseInt(dimensions[1], 10);
          
          // Validate dimensions
          if (isNaN(width) || isNaN(height) || width <= 0 || height <= 0) {
            showSnackbar('Invalid image dimensions. Please check your aspect ratio settings.', 'error');
            setIsGenerating(false);
            return;
          }

          const numImagesToGenerate = canvaSettings.number_of_images || 1;

          // Check if this is a LoRA model using modelType from API response
          const isLoRAModel = canvaSettings.modelType === 'text-to-image-lora';

          // Check if this is an edit model using modelType from API response
          const isEditModel = canvaSettings.modelType === 'image-editing';
          
          // For edit models, ensure image_urls is set
          // Ensure imageUrls is always an array format
          let imageUrls: string[] = Array.isArray(canvaSettings.image_urls) 
            ? canvaSettings.image_urls 
            : (canvaSettings.image_urls ? [canvaSettings.image_urls] : []);
          
          if (isEditModel) {
            // If image_urls is not set in settings, try to get it from uploadedImage
            if (!imageUrls || imageUrls.length === 0) {
              if (uploadedImage) {
                // Ensure it's an array
                imageUrls = Array.isArray(uploadedImage) ? uploadedImage : [uploadedImage];
              } else {
                // If still no image URL, show an error
                showSnackbar('Please upload or select an image to edit.', 'error');
                setIsGenerating(false);
                return;
              }
            } else {
              console.log('Using image_urls from settings:', imageUrls);
            }
            // Final check - ensure imageUrls is set and is an array
            if (!Array.isArray(imageUrls) || imageUrls.length === 0) {
              showSnackbar('Please upload or select an image to edit.', 'error');
              setIsGenerating(false);
              return;
            }
          }

          const aspectRatio = canvaSettings.aspectRatio;
          const isCustomAspectRatio = aspectRatio && aspectRatio.toLowerCase() === 'custom';

          let request: GenerateImageRequest;

          if (isEditModel) {
            // Special payload format for ALL image editing models
            request = {
              prompt: editImagePrompt,
              model_id: canvaSettings.modelId || '',
              width: width,
              height: height,
              num_images: numImagesToGenerate,
              image_format: canvaSettings.imageFormat || 'jpeg',
              operation: 'generate',
              model_parameters: {
                image_urls: imageUrls,
                aspect_ratio: aspectRatio || '16:9',
                num_images: numImagesToGenerate
              },
              image_categories: canvaSettings.style 
                ? ['art_style'] 
                : canvaSettings.project 
                  ? ['persona'] 
                  : ['vanilla']
            };

            if (canvaSettings.style) {
              request.artStyleId = canvaSettings.style.id;
            } else if (canvaSettings.project) {
              request.artStyleId = canvaSettings.project.id;
            }

            if (canvaSettings.project) {
              request.project_id = canvaSettings.project.projectID;
            }
          } else {
            // Standard payload format for other models
            request = {
              prompt: editImagePrompt,
              numImages: numImagesToGenerate,
              
              // For LoRA models, send modelId to identify the model (especially important for vanilla images)
              // For non-LoRA models, send modelId if it exists
              ...(canvaSettings.modelId ? { modelId: canvaSettings.modelId } : {}),
              
              // For LoRA models, also send model name so backend knows which base model was used
              ...(isLoRAModel && canvaSettings.modelName ? { modelName: canvaSettings.modelName } : {}),
              
              ...(canvaSettings.style ? { artStyleId: canvaSettings.style?.id } : {}),
              ...(canvaSettings.project ? { artStyleId: canvaSettings.project?.id } : {}),
              ...(canvaSettings.project ? { project_id: canvaSettings.project?.projectID } : {}),
              
              // Add dynamic parameters based on model
              // Resolution for Google Nano Banana Pro
              ...(canvaSettings.resolution ? { resolution: canvaSettings.resolution } : {}),
              
              // Image URLs for editing models - REQUIRED for edit models
              // CRITICAL: Always send as array format [url1, url2, url3]
              ...(Array.isArray(imageUrls) && imageUrls.length > 0 ? { image_urls: imageUrls } : {}),
            };

            // For LoRA models, structure parameters according to API spec
            if (isLoRAModel) {
              // Initialize model_parameters for LoRA models
              request.model_parameters = request.model_parameters || {};
              
              // Send art_style_id in model_parameters (required by LoRA API spec when art style/project is selected)
              const artStyleId = canvaSettings.style?.id || canvaSettings.project?.id;
              if (artStyleId) {
                request.model_parameters.art_style_id = artStyleId;
              }
              
              // Send num_images in model_parameters for LoRA models
              request.model_parameters.num_images = numImagesToGenerate;
            }

            // Handle aspect ratio and dimensions for ALL models
            // Always send aspect_ratio at top level (never send width/height at top level)
            // Explicitly remove width/height to ensure they're never sent at top level
            delete request.width;
            delete request.height;
            
            if (aspectRatio) {
              // Send aspect_ratio at top level for all models
              request.aspect_ratio = aspectRatio;
              
              // If Custom, include custom_image_size in model_parameters
              if (isCustomAspectRatio) {
                request.model_parameters = {
                  ...(request.model_parameters || {}),
                  custom_image_size: {
                    width: width,
                    height: height
                  }
                };
              }
              
              // For LoRA models, also send image_size in model_parameters (mapped from feAspectRatioMapping)
              if (isLoRAModel && canvaSettings.feAspectRatioMapping) {
                const mappedImageSize = canvaSettings.feAspectRatioMapping[aspectRatio];
                if (mappedImageSize) {
                  request.model_parameters = {
                    ...(request.model_parameters || {}),
                    image_size: mappedImageSize
                  };
                }
              }
            } else {
              // If aspectRatio is not set, we still don't send width/height
              // Log a warning to help debug
              console.warn('aspectRatio is not set in canvaSettings. Please ensure aspect ratio is selected in the UI.');
            }
            // Never send width/height at top level for any model

            // Final check for edit models
            if (isEditModel && (!request.image_urls || request.image_urls.length === 0)) {
              showSnackbar('Image URL is required for editing models. Please upload an image.', 'error');
              setIsGenerating(false);
              return;
            }

            // Set image categories: use state when "Use Persona" (mypersona), else from settings
            if (imageCategories.includes('mypersona')) {
              request.imageCategories = imageCategories;
            } else if (canvaSettings.style) {
              request.imageCategories = ['art_style'];
            } else if (canvaSettings.project) {
              request.imageCategories = ['persona'];
            } else {
              request.imageCategories = ['vanilla'];
            }
          }

          try {
            const { data }: { data: GenerateImageResponse | any } = await imageGenerationClient.post('/images', request);
          
            // Log response for debugging
            console.log('API Response:', data);
            console.log('Response type:', Array.isArray(data) ? 'array' : typeof data);
            console.log('Has images property:', data?.images);
            console.log('Images array:', data?.images);
          
            // Handle different response structures
            let processedData: GenerateImageResponse;
            if (Array.isArray(data)) {
            // If response is an array of images directly
              processedData = {
                images: data.map((img: any) => ({
                  id: img.id,
                  url: img.url,
                  artStyleId: img.artStyleId,
                  imageCategories: img.imageCategories,
                  projectId: img.projectId,
                  width: img.width,
                  height: img.height
                }))
              };
            } else if (data?.images && Array.isArray(data.images)) {
            // If response has images array
              processedData = {
                images: data.images.map((img: any) => ({
                  id: img.id,
                  url: img.url,
                  artStyleId: img.artStyleId,
                  imageCategories: img.imageCategories,
                  projectId: img.projectId,
                  width: img.width,
                  height: img.height
                }))
              };
            } else {
            // If response is a single image object (width/height might be at top level or in the object)
              processedData = {
                images: [{
                  id: data.id,
                  url: data.url,
                  artStyleId: data.artStyleId,
                  imageCategories: data.imageCategories,
                  projectId: data.projectId,
                  width: data.width,
                  height: data.height
                }]
              };
            }
          
            console.log('Processed data:', processedData);
            console.log('Number of images in processed data:', processedData?.images?.length);
          
            // Validate that we have images
            if (!processedData?.images || processedData.images.length === 0) {
              console.error('No images found in response:', data);
              showSnackbar('No images were returned from the API. Please try again.', 'error');
              setIsGenerating(false);
              return;
            }
          
            // Fetch full image details (including prompt and modelName) for all generated images
            const fallbackPrompt = request.prompt || prompt || '';
            const fallbackModelName = canvaSettings.modelName || canvaSettings.model || '';
            
            const imagesWithFullDetails = await Promise.all(
              processedData.images.map(async (img) => {
                const fullDetails = await fetchFullMediaDetails(
                  img.id,
                  'image',
                  fallbackPrompt,
                  fallbackModelName
                );
                return {
                  ...img,
                  prompt: fullDetails.prompt,
                  modelName: fullDetails.modelName
                };
              })
            );

            // Create updated response with full details
            const processedDataWithDetails: GenerateImageResponse = {
              images: imagesWithFullDetails
            };

            // Add the new response to the generated images array (stacking like /canvas)
            setGeneratedImages(prev => {
              const updated = [processedDataWithDetails, ...prev];
              console.log('Updated generatedImages with full details:', updated);
              return updated;
            });
          
            const newImage = processedDataWithDetails?.images?.[0];
            console.log('New image to display:', newImage);

            // Only set uploadedImage if there's only 1 image (for single image view)
            // If there are multiple images, don't set uploadedImage so multiple images view is shown
            if (processedData?.images?.length === 1 && newImage?.url && newImage?.id) {
            // Use thumbnail URL from generation response (don't call getHdImageUrl here to avoid credit deduction)
            // HD URL will be fetched only when user explicitly clicks download
              setUploadedImage(newImage.url);
              setCurrentImageId(newImage.id);
              pushToHistory(newImage.url, newImage.id);
              setIsUploaded(false);
              // Update image categories if returned
              if (newImage.imageCategories) {
                setImageCategories(newImage.imageCategories);
              }

              // Update canvaSettings with the actual dimensions returned from API
              if (newImage.width && newImage.height) {
                setCanvaSettings(settings => ({
                  ...settings,
                  image_dimensions: `${newImage.width}x${newImage.height}`
                }));
              }

              // Update image size for display
              const newImageSize = await getImageSize(newImage.url);
              setImageSize(newImageSize);

              // Update canvas dimensions to fit the image
              const mainArea = document.querySelector('.canvas-main-area');
              if (mainArea) {
                const containerWidth = mainArea.clientWidth * 0.95;
                const containerHeight = STATIC_PAINT_AREA_HEIGHT;

                const imageRatio = newImageSize.width / newImageSize.height;
                let displayWidth, displayHeight;

                if (containerWidth / containerHeight > imageRatio) {
                  displayHeight = containerHeight;
                  displayWidth = displayHeight * imageRatio;
                } else {
                  displayWidth = containerWidth;
                  displayHeight = displayWidth / imageRatio;
                }

                setCanvasDimensions({ width: containerWidth, height: containerHeight });
                setImageBox({
                  x: (containerWidth - displayWidth) / 2,
                  y: (containerHeight - displayHeight) / 2,
                  width: displayWidth,
                  height: displayHeight
                });
              }
            } else if (processedData?.images?.length > 1) {
            // Multiple images generated - don't set uploadedImage, clear it to show multiple images view
              console.log('Multiple images generated, clearing uploadedImage to show stacked view');
              console.log('Number of images:', processedData.images.length);
              setUploadedImage(null);
              setCurrentImageId(null);
            
              // Update image categories from first image if returned
              if (newImage?.imageCategories) {
                setImageCategories(newImage.imageCategories);
              }

              // Update canvaSettings with the actual dimensions from first image if available
              if (newImage?.width && newImage?.height) {
                setCanvaSettings(settings => ({
                  ...settings,
                  image_dimensions: `${newImage.width}x${newImage.height}`
                }));
              }
            }
          } catch (error: any) {
            // Log full error for debugging
            
            // Check for content policy violation (422 status or content_policy_violation type)
            const isContentPolicyViolation = 
              error?.response?.status === 422 || 
              error?.response?.data?.type === 'content_policy_violation';
            
            if (isContentPolicyViolation) {
              showSnackbar('The content could not be processed because it contained material flagged by a content checker.', 'error');
              setIsGenerating(false);
              return;
            }
            
            // Check if it's a dimension-related error
            const isDimensionError = (err: any): boolean => {
              if (!err) return false;
              
              const errorMessage = 
                err?.response?.data?.errors?.[0]?.message ||
                err?.response?.data?.error ||
                err?.response?.data?.message ||
                err?.message ||
                '';
              
              const errorString = JSON.stringify(err?.response?.data || err || '').toLowerCase();
              
              // Check for dimension-related keywords
              const dimensionKeywords = [
                'image_size',
                'width',
                'height',
                'dimension',
                'invalid parameter',
                'invalid_request',
                'custom_image_size'
              ];
              
              return dimensionKeywords.some(keyword => 
                errorString.includes(keyword) || errorMessage.toLowerCase().includes(keyword)
              );
            };
            
            if (isDimensionError(error)) {
              showSnackbar('Please enter valid width and height.', 'warning');
            } else {
              const errorMessage =
                error?.response?.data?.errors?.[0]?.message ||
                error?.response?.data?.error ||
                error?.message ||
                error?.response?.data?.message ||
                'Something went wrong while generating the image.';

              showSnackbar(errorMessage, 'error');
            }
          } finally {
            setIsGenerating(false);
          }
        }
      }
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.errors?.[0]?.message ||
        error?.message ||
        error?.response?.data?.message ||
        'Something went wrong.';

      showSnackbar(errorMessage, 'error');
    } finally {
      setIsGenerating(false);
    }
  };

  useEffect(() => {
    if (!canvasApiRef.current) {
      canvasApiRef.current = new CanvasApi(() => {
        console.log('Canvas updated');
      });
    }

    // Register canvas handlers for image generation features from /canvas route
    if (canvasApiRef.current) {
      const handlers = {
        getPrompt: () => editImagePrompt || prompt, // Use editImagePrompt if available, otherwise use prompt
        setCanvasSettings: setCanvaSettings,
        setImagesResponse: () => {}, // Not used in editor mode, but required by CanvasApi
        showSnackbar: showSnackbar,
        setIsGenerating: setIsGenerating,
        scrollToTop: () => {
          // Scroll to top if needed
        },
        getUploadedImage: () => uploadedImage // Return the current uploaded image URL for edit models
      };
      canvasApiRef.current.registerCanvas(handlers);
      canvasApiRef.current.setCanvasSettings(canvaSettings);
    }
  }, [editImagePrompt, prompt, setCanvaSettings, showSnackbar, canvaSettings, uploadedImage]);

  useEffect(() => {
    if (props.setToolComponent) {
      if (activeTool) {
        const ToolComponent = toolComponentMap[activeTool];
        if (ToolComponent && canvasApiRef.current) {
          const actionHandler =
            activeTool === 'Outpaint' ? handleOutpaintAction : (handleToolAction as (newImage: string | { id: string; url: string }) => void);

          const toolElement = (
            <Box
              sx={{
                width:"286px",
              }}
            >
              <ToolComponent
                onAction={actionHandler}
                canvasApi={canvasApiRef.current!}
                id={currentImageId}
                initialPrompt={prompt}
                subjectImage={uploadedImage}
                pushToHistory={pushToHistory}
                setIsGenerating={setIsGenerating}
                setCurrentImageId={setCurrentImageId}
                onAspectRatioChange={handleAspectRatioChange}
                onDimensionsChange={handleDimensionsChange}
                currentCanvasDimensions={imageBox}
                currentGenerationDimensions={generationDimensions}
                imageCategories={imageCategories}
                setImageCategories={setImageCategories}
                projectId={projectId}
                canvasWidth="true"
              />
            </Box>
          );
          props.setToolComponent(toolElement);
        } else {
          props.setToolComponent(null);
        }
      } else {
        props.setToolComponent(null);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTool, currentImageId, prompt, uploadedImage, imageBox, generationDimensions, imageCategories, projectId]);

  return (
    <>
      {/* Modal container for replace background and edit with text features from /canvas route */}
      {canvasApiRef.current && <CanvasModalContainer canvasApi={canvasApiRef.current} />}
      
      <Box sx={{ mb: props.marginBottomEditing!==undefined ? props.marginBottomEditing:9 }}>
        {props.exiteBoolean===false?<></>:<><Header /></>}
        
      </Box>
      {props.canvaBoardDisplay==="grid"?<Box display={'grid'} gridTemplateColumns="3fr 1fr " gap="10px" pt={1} position={'relative'} >
        <Box>
          <EditorToolbar
            exitBoolean={props.exiteBoolean}
            Exiticon={Exiticon}
            clearSession={clearSession}
            activeTool={activeTool}
            isGenerating={isGenerating}
            isOutpaintActive={isOutpaintActive}
            setActiveTool={setActiveTool}
            setIsOutpaintActive={setIsOutpaintActive}
            setHasOutpaintAspect={setHasOutpaintAspect}
            setIsOutpaintFrameLocked={setIsOutpaintFrameLocked}
            removeBackground={removeBackground}
            upscaleImage={upscaleImage}
            deblurImage={deblurImage}
            denoiseImage={denoiseImage}
            retouchImage={retouchImage}
            contentTypeTab={props.contentTypeTab}
          />
          
          <CanvasWorkSpace
            uploadedImage={uploadedImage}
            isGenerating={isGenerating}
            isOutpaintActive={isOutpaintActive}
            hasOutpaintAspect={hasOutpaintAspect}
            isOutpaintFrameLocked={isOutpaintFrameLocked}
            canvasDimensions={canvasDimensions}
            imageBox={imageBox}
            editImagePrompt={editImagePrompt}
            isDownloading={isDownloading}
            locationState={location.state}
            getRootProps={getRootProps}
            getInputProps={getInputProps}
            handleMouseDown={handleMouseDown}
            setEditImagePrompt={setEditImagePrompt}
            replaceUsingPrompt={replaceUsingPrompt}
            download={download}
            handleDeleteImage={handleDeleteImage}
            contentTypeTab={props.contentTypeTab}
          />
        </Box>

        <Box sx={{ flexGrow: 1, width: '100%', display: 'flex', justifyContent: 'center', pt: 1.5 }}>
          {activeTool &&
            (() => {
              const ToolComponent = toolComponentMap[activeTool];

              const actionHandler =
                activeTool === 'Outpaint' ? handleOutpaintAction : (handleToolAction as (newImage: string | { id: string; url: string }) => void);

              return ToolComponent && canvasApiRef.current ? (
                <ToolComponent
                  onAction={actionHandler}
                  canvasApi={canvasApiRef.current!}
                  id={currentImageId}
                  initialPrompt={prompt}
                  subjectImage={uploadedImage}
                  pushToHistory={pushToHistory}
                  setIsGenerating={setIsGenerating}
                  setCurrentImageId={setCurrentImageId}
                  onAspectRatioChange={handleAspectRatioChange}
                  onDimensionsChange={handleDimensionsChange}
                  currentCanvasDimensions={imageBox}
                  currentGenerationDimensions={generationDimensions}
                  imageCategories={imageCategories}
                  setImageCategories={setImageCategories}
                  projectId={projectId}
                />
              ) : null;
            })()}
        </Box>
        

        <UndoRedoControls
          isGenerating={isGenerating}
          handleUndo={handleUndo}
          handleRedo={handleRedo}
        />
        
      </Box>:
        <Box >
          {/* Left side: EditorToolbar + CanvasWorkSpace FairGen 2.0*/}
          <Box>
            <EditorToolbar
              exitBoolean={props.exiteBoolean}
              Exiticon={Exiticon}
              clearSession={clearSession}
              activeTool={activeTool}
              isGenerating={isGenerating}
              isOutpaintActive={isOutpaintActive}
              setActiveTool={setActiveTool}
              setIsOutpaintActive={setIsOutpaintActive}
              setHasOutpaintAspect={setHasOutpaintAspect}
              setIsOutpaintFrameLocked={setIsOutpaintFrameLocked}
              removeBackground={removeBackground}
              upscaleImage={upscaleImage}
              deblurImage={deblurImage}
              denoiseImage={denoiseImage}
              retouchImage={retouchImage}
              viewMode={viewMode}
              setViewMode={setViewMode}
              contentTypeTab={props.contentTypeTab}
            />
          </Box>


          {/* FairGen 2.0 Workspace */}
          <Box mr="12px">
            <CanvasWorkSpace
              uploadedImage={uploadedImage}
              isGenerating={isGenerating}
              isOutpaintActive={isOutpaintActive}
              hasOutpaintAspect={hasOutpaintAspect}
              isOutpaintFrameLocked={isOutpaintFrameLocked}
              canvasDimensions={canvasDimensions}
              imageBox={imageBox}
              editImagePrompt={editImagePrompt}
              isDownloading={isDownloading}
              locationState={location.state}
              getRootProps={getRootProps}
              getInputProps={getInputProps}
              handleMouseDown={handleMouseDown}
              setEditImagePrompt={setEditImagePrompt}
              replaceUsingPrompt={replaceUsingPrompt}
              download={download}
              handleDeleteImage={handleDeleteImage}
              handleUndo={handleUndo}
              handleRedo={handleRedo}
              canvaImageBoolean={true}
              generatedImages={generatedImages}
              viewMode={viewMode}
              contentTypeTab={props.contentTypeTab}
              onImageSelect={(async (_imageUrl: string, imageId: string) => {
                // When an image is selected, set it as the current image for editing
                // Find the selected image in generatedImages to get its dimensions
                let selectedImage = null;
                for (const response of generatedImages) {
                  const found = response.images.find(img => img.id === imageId);
                  if (found) {
                    selectedImage = found;
                    break;
                  }
                }

                // Switch to edit mode when loading an image for editing
                switchToEditMode();

                const hdUrl = await getHdImageUrl(imageId);
                setUploadedImage(hdUrl);
                setCurrentImageId(imageId);

                // Update canvaSettings with the selected image's dimensions if available
                if (selectedImage?.width && selectedImage?.height) {
                  setCanvaSettings(settings => ({
                    ...settings,
                    image_dimensions: `${selectedImage.width}x${selectedImage.height}`
                  }));
                }

                // Update image size for display
                const newImageSize = await getImageSize(hdUrl);
                setImageSize(newImageSize);
                // Update canvas dimensions to fit the image
                const mainArea = document.querySelector('.canvas-main-area');
                if (mainArea) {
                  const containerWidth = mainArea.clientWidth * 0.95;
                  const containerHeight = STATIC_PAINT_AREA_HEIGHT;
                  const imageRatio = newImageSize.width / newImageSize.height;
                  let displayWidth, displayHeight;
                  if (containerWidth / containerHeight > imageRatio) {
                    displayHeight = containerHeight;
                    displayWidth = displayHeight * imageRatio;
                  } else {
                    displayWidth = containerWidth;
                    displayHeight = displayWidth / imageRatio;
                  }
                  setCanvasDimensions({ width: containerWidth, height: containerHeight });
                  setImageBox({
                    x: (containerWidth - displayWidth) / 2,
                    y: (containerHeight - displayHeight) / 2,
                    width: displayWidth,
                    height: displayHeight
                  });
                }

                // Switch view mode to 'list' to show the single image paint area
                setViewMode('list');
              })}
              currentImageId={currentImageId}
            />
          </Box>
          
        </Box>}
      

      

    </>
  );
};

export default CanvasAIEditor;